import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Avatar, 
  Paper, 
  Divider, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  CircularProgress, 
  Alert, 
  Tabs, 
  Tab,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Snackbar,
  IconButton,
  Tooltip,
  Grid,
  Autocomplete,
  Chip,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Checkbox
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Delete as DeleteIcon, AddCircleOutline as AddCircleOutlineIcon, School as SchoolIcon, CameraAlt as CameraAltIcon } from '@mui/icons-material';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { ref, get, remove, set, update } from 'firebase/database';
import { database } from '../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import { Student } from '../types/Student';
import { userToStudentAdapter } from '../utils/userToStudentAdapter';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../hooks/useAuth';
import { useSchools } from '../contexts/SchoolsContext';
import { usePrograms } from '../contexts/ProgramsContext';
import { activityLogService } from '../services/activityLogService';
import { eligibilityMigrationService } from '../services/eligibilityMigrationService';
import { eligibilityRefreshService } from '../services/eligibilityRefreshService';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

// Add CSS animation for pulse effect
const pulseAnimation = `
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.7; }
    100% { opacity: 1; }
  }
`;



interface Course {
  id: number;
  name: string;
  grade: string;
  gradeType: 'actual' | 'projected';
  courseName?: string;
}

interface DisplayCourse {
  name: string;
  courseName: string;
  grade: string;
  gradeType: 'actual' | 'projected';
  year: string;
}

interface FirebaseCourse {
  CourseLastUpdated?: string;
  CourseType?: string;
  Grade?: string;
  Level?: string;
  Name?: string;
  Notes?: string;
  Prerequisite?: string;
  Segment?: string;
}

interface CourseOption {
  code: string;
  description: string;
}

const UserStudentProfile: React.FC = () => {
  const { user: currentUser, loading: userLoading, error: userError } = useUser();
  const { user: authUser } = useAuth();
  const { schools } = useSchools();
  const { clearEligibility } = usePrograms();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine if current user is guidance user and if viewing own profile
  const isGuidanceUser = currentUser?.isGuidanceUser || false;
  // For now, UserStudentProfile always shows current user's profile
  // In the future, this could check URL params to determine if viewing another student
  const isViewingOwnProfile = true; // Currently always viewing own profile

  // Calculate which tabs should be visible and create mapping
  const visibleTabs: number[] = [];
  if (isGuidanceUser && isViewingOwnProfile) {
    // Guidance user viewing own profile: only show Courses & Grades (0) and Account (4)
    visibleTabs.push(0, 4);
  } else if (isGuidanceUser && !isViewingOwnProfile) {
    // Guidance user viewing another student: show all except Account (4)
    visibleTabs.push(0, 1, 2, 3);
  } else {
    // Regular user: show all tabs
    visibleTabs.push(0, 1, 2, 3, 4);
  }

  // Map selectedTab to actual tab index (handle case where tabs are hidden)
  const getActualTabIndex = (displayIndex: number) => {
    if (visibleTabs.includes(displayIndex)) {
      return displayIndex;
    }
    // If selected tab is hidden, default to first visible tab
    return visibleTabs[0] || 0;
  };

  // Add CSS animation to document head
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = pulseAnimation;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Note: Removed page load clearing to prevent interference with recalculation snackbar

  
  // Helper function to get the correct spelling based on user's country
  const getAwardsHonoursLabel = () => {
    return currentUser?.country === 'US' ? 'Awards & Honors' : 'Awards & Honours';
  };
  
  const getAwardHonourLabel = () => {
    return currentUser?.country === 'US' ? 'Award/Honor' : 'Award/Honour';
  };
  
  const getAwardHonourSingular = () => {
    return currentUser?.country === 'US' ? 'award/honor' : 'award/honour';
  };
  const [selectedTab, setSelectedTab] = useState(() => {
    // Check if there's a tab parameter in the URL
    const tabParam = searchParams.get('tab');
    if (tabParam === 'academics') return 0;
    if (tabParam === 'athletics') return 1;
    if (tabParam === 'work') return 2;
    if (tabParam === 'clubs') return 3;
    if (tabParam === 'account') return 4;
    return 0; // Default to academics tab
  });

  // Handle URL parameter changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'academics') setSelectedTab(0);
    else if (tabParam === 'athletics') setSelectedTab(1);
    else if (tabParam === 'work') setSelectedTab(2);
    else if (tabParam === 'clubs') setSelectedTab(3);
    else if (tabParam === 'account') setSelectedTab(4);
  }, [searchParams]);

  // Ensure selectedTab is valid for guidance users (move this after selectedTab declaration)
  useEffect(() => {
    if (!visibleTabs.includes(selectedTab)) {
      setSelectedTab(visibleTabs[0] || 0);
    }
  }, [isGuidanceUser, isViewingOwnProfile, selectedTab, visibleTabs]);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [isRecalculatingEligibility, setIsRecalculatingEligibility] = useState(false);
  const [recalculationProgress, setRecalculationProgress] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showCompletionAtTop, setShowCompletionAtTop] = useState(false);

  // Debug snackbar state changes
  React.useEffect(() => {
    console.log('🔄 Snackbar state changed - showSuccessMessage:', showSuccessMessage, 'successMessage:', successMessage, 'isRecalculatingEligibility:', isRecalculatingEligibility);
  }, [showSuccessMessage, successMessage, isRecalculatingEligibility]);

  // Real-time UI updates for progress bars - DISABLED to prevent blinking
  // React.useEffect(() => {
  //   console.log('🔄 useEffect triggered - isRecalculatingEligibility:', isRecalculatingEligibility, 'recalculationProgress:', recalculationProgress);
  //   
  //   if (isRecalculatingEligibility) {
  //     console.log('🔄 UI Update: Progress bar is active, updating UI');
  //     
  //     // Update progress messages in real-time
  //     const progressInterval = setInterval(() => {
  //       console.log('🔄 Progress interval triggered - current progress:', recalculationProgress);
  //       if (recalculationProgress === 'Starting eligibility recalculation...') {
  //         console.log('🔄 Updating to Preparing calculation...');
  //         setRecalculationProgress('Preparing calculation...');
  //         setSuccessMessage('Preparing calculation...');
  //         setShowSuccessMessage(true);
  //       } else if (recalculationProgress === 'Preparing calculation...') {
  //         console.log('🔄 Updating to Calculating program eligibility...');
  //         setRecalculationProgress('Calculating program eligibility...');
  //         setSuccessMessage('Calculating program eligibility...');
  //         setShowSuccessMessage(true);
  //       } else if (recalculationProgress === 'Calculating program eligibility...') {
  //         console.log('🔄 Progress is at Calculating program eligibility... - ensuring snackbar is visible');
  //         // Only update if not already showing to prevent rapid toggling
  //         if (!showSuccessMessage) {
  //           setShowSuccessMessage(true);
  //         }
  //       }
  //     }, 2000);

  //     return () => {
  //       console.log('🔄 Clearing progress interval');
  //       clearInterval(progressInterval);
  //     };
  //   }
  // }, [isRecalculatingEligibility, recalculationProgress]);
  

  
  // Clubs & Activities management
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<{ id: string; name: string } | null>(null);
  const [newActivity, setNewActivity] = useState({
    interest: '',
    startGrade: '',
    endGrade: '',
    description: '',
    link: ''
  });

  // Work & Volunteering management
  const [showAddWorkVolunteer, setShowAddWorkVolunteer] = useState(false);
  const [showDeleteWorkVolunteerConfirmation, setShowDeleteWorkVolunteerConfirmation] = useState(false);
  const [workVolunteerToDelete, setWorkVolunteerToDelete] = useState<{ id: string; name: string } | null>(null);
  const [newWorkVolunteer, setNewWorkVolunteer] = useState({
    position: '',
    employer: '',
    startGrade: '',
    endGrade: '',
    description: ''
  });

  // Athletics management
  const [showAddAthletics, setShowAddAthletics] = useState(false);
  const [showDeleteAthleticsConfirmation, setShowDeleteAthleticsConfirmation] = useState(false);
  const [athleticsToDelete, setAthleticsToDelete] = useState<{ id: string; name: string } | null>(null);
  const [newAthletics, setNewAthletics] = useState({
    sport: '',
    position: '',
    startGrade: '',
    endGrade: '',
    description: '',
    jerseyNumber: '',
    level: '',
    youtubeLink: '',
    articleLink: '',
    isContinue: false
  });

  // Academic Awards & Honours management
  const [showAddAwardsHonours, setShowAddAwardsHonours] = useState(false);
  const [showDeleteAwardsHonoursConfirmation, setShowDeleteAwardsHonoursConfirmation] = useState(false);
  const [awardsHonoursToDelete, setAwardsHonoursToDelete] = useState<{ id: string; name: string } | null>(null);
  const [newAwardsHonours, setNewAwardsHonours] = useState({
    title: '',
    grade: '',
    yearReceived: '',
    description: ''
  });

  // Academic Extenuating Circumstances management
  const [showAddExtenuatingCircumstances, setShowAddExtenuatingCircumstances] = useState(false);
  const [showDeleteExtenuatingCircumstancesConfirmation, setShowDeleteExtenuatingCircumstancesConfirmation] = useState(false);
  const [extenuatingCircumstancesToDelete, setExtenuatingCircumstancesToDelete] = useState<{ id: string; name: string } | null>(null);
  const [newExtenuatingCircumstances, setNewExtenuatingCircumstances] = useState({
    description: ''
  });

  // Grade conversion functions (same as OnboardingModal)
  const calculateGradeFromClassYear = (classYear: string): number => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // January is 0, so add 1
    const classYearNum = parseInt(classYear);
    
    if (currentMonth >= 7) {
      // July or later
      if (classYearNum === currentYear || classYearNum === currentYear + 1) return 12;
      if (classYearNum === currentYear + 2) return 11;
      if (classYearNum === currentYear + 3) return 10;
      if (classYearNum === currentYear + 4) return 9;
      return 10; // classYearNum > currentYear + 4
    } else {
      // Before July
      if (classYearNum === currentYear) return 12;
      if (classYearNum === currentYear + 1) return 11;
      if (classYearNum === currentYear + 2) return 10;
      if (classYearNum === currentYear + 3) return 9;
      return 10; // classYearNum >= currentYear + 3
    }
  };

  const calculateClassYearFromGrade = (grade: number): string => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // January is 0, so add 1
    
    if (grade === 12) {
      if (currentMonth < 7) {
        return currentYear.toString();
      } else {
        return (currentYear + 1).toString();
      }
    } else if (grade === 11) {
      if (currentMonth < 7) {
        return (currentYear + 1).toString();
      } else {
        return (currentYear + 2).toString();
      }
    } else if (grade === 10) {
      if (currentMonth < 7) {
        return (currentYear + 2).toString();
      } else {
        return (currentYear + 3).toString();
      }
    } else if (grade === 9) {
      if (currentMonth < 7) {
        return (currentYear + 3).toString();
      } else {
        return (currentYear + 4).toString();
      }
    }
    
    return (currentYear + 1).toString(); // Default fallback
  };

  // Account editing state
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [accountFormData, setAccountFormData] = useState({
    firstName: '',
    lastName: '',
    schoolName: '',
    schoolID: '',
    city: '',
    province: '',
    classYear: '',
    grade: 12, // Add grade field, default to 12
    phoneNumber: '',
    email: '',
    country: '',
    primaryLanguage: '',
    status: '',
    gender: '',
    accountType: 1, // Default to Student (1)
    isInternational: false,
    instagramUrl: '',
    twitterUrl: '',
    linkedinUrl: ''
  });

  // Avatar upload state
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // High schools state for dropdown
  const [highSchools, setHighSchools] = useState<Array<{id: string, name: string, city?: string}>>([]);
  const [loadingSchools, setLoadingSchools] = useState(false);




  // Academics section state
  const [grade12CourseEntries, setGrade12CourseEntries] = useState<Course[]>([
    { id: 1, name: 'ENG4U', grade: '', gradeType: 'projected' },
    { id: 2, name: 'MHF4U', grade: '', gradeType: 'projected' },
    { id: 3, name: 'MCV4U', grade: '', gradeType: 'projected' },
    { id: 4, name: 'MDM4U', grade: '', gradeType: 'projected' },
    { id: 5, name: 'SCH4U', grade: '', gradeType: 'projected' },
    { id: 6, name: 'SBI4U', grade: '', gradeType: 'projected' },
    { id: 23, name: '', grade: '', gradeType: 'projected' },
    { id: 24, name: '', grade: '', gradeType: 'projected' },
  ]);
  const [grade11CourseEntries, setGrade11CourseEntries] = useState<Course[]>([
    { id: 7, name: 'ENG3U', grade: '', gradeType: 'projected' },
    { id: 8, name: 'MCR3U', grade: '', gradeType: 'projected' },
    { id: 9, name: 'SBI3U', grade: '', gradeType: 'projected' },
    { id: 18, name: 'SCH3U', grade: '', gradeType: 'projected' },
    { id: 19, name: 'SPH3U', grade: '', gradeType: 'projected' },
    { id: 20, name: 'HSP3U', grade: '', gradeType: 'projected' },
    { id: 21, name: 'CLU3M', grade: '', gradeType: 'projected' },
    { id: 22, name: 'FSF3U', grade: '', gradeType: 'projected' },
  ]);
  const [grade10CourseEntries, setGrade10CourseEntries] = useState<Course[]>([
    { id: 10, name: 'ENG2D', grade: '', gradeType: 'projected' },
    { id: 11, name: 'MPM2D', grade: '', gradeType: 'projected' },
    { id: 12, name: 'SNC2D', grade: '', gradeType: 'projected' },
    { id: 13, name: 'CHC2D', grade: '', gradeType: 'projected' },
    { id: 14, name: 'CHV2O', grade: '', gradeType: 'projected' },
    { id: 15, name: 'GLC2O', grade: '', gradeType: 'projected' },
    { id: 16, name: 'FSF2O', grade: '', gradeType: 'projected' },
    { id: 17, name: 'BBI2O', grade: '', gradeType: 'projected' },
  ]);
  const [grade12CourseOptions, setGrade12CourseOptions] = useState<CourseOption[]>([]);
  const [grade11CourseOptions, setGrade11CourseOptions] = useState<CourseOption[]>([]);
  const [grade10CourseOptions, setGrade10CourseOptions] = useState<CourseOption[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [isEditingGrades, setIsEditingGrades] = useState(false);
  const [shouldEnterEditMode, setShouldEnterEditMode] = useState(false);
  const [hasBackupGrades, setHasBackupGrades] = useState(false);

  // Validation error state
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Transform current user data to Student format
  const student = userToStudentAdapter(currentUser);

  // Create a default student object if no profile exists
  const defaultStudent: Student = {
    id: authUser?.uid || '',
    firstName: '',
    lastName: '',
    grade: '',
    school: '',
    city: '',
    province: '',
    interests: [],
    favoriteSchools: [],
    achievements: [],
    gpa: 0,
    email: '',
    status: 'Active',
    enrollmentDate: new Date().toISOString(),
    completedCourses: [],
    clubs: [],
    athletics: [],
    activities: [],
    workExperience: [],
    registeredEventIds: [],
    athleticsSportsProfile: {},
    athleticsAwardsProfile: {},
    academicAwardsHonoursProfile: {},
    academicExtenuatingCircumstancesProfile: {},
    workVolunteerExpProfile: {},
    interestsActivitiesProfile: {},
    eligibilityReport: {}
  };

  // Use the actual student if found, otherwise use the default
  const studentToDisplay = student || defaultStudent;
  const isNewProfile = !student;
  


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle incoming state to set selected tab and enter edit mode
  useEffect(() => {
    if (location.state?.selectedTab !== undefined) {
      setSelectedTab(location.state.selectedTab);
      
      // If editGrades flag is set, mark that we should enter edit mode after grades load
      if (location.state.editGrades === true) {
        setShouldEnterEditMode(true);
      }
      
      // If hasBackup flag is set, mark that we have backup grades to restore on cancel
      if (location.state.hasBackup === true) {
        setHasBackupGrades(true);
      }
      
      // Clear the state to prevent it from persisting
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Fetch courses from Firebase when component loads
  useEffect(() => {
    fetchCoursesFromFirebase();
  }, [authUser?.uid]);

  // Ensure grades are properly loaded when student data changes
  useEffect(() => {
    console.log('🔄 Student data changed:', student);
    if (student && student.grades && Object.keys(student.grades).length > 0) {
      console.log('📚 Grades loaded for student:', Object.keys(student.grades).length, 'courses');
      console.log('📚 Grades data:', student.grades);
    } else {
      console.log('📚 No grades found in student data');
    }
  }, [student?.grades]);

  // Handle entering edit mode after copying courses
  useEffect(() => {
    if (shouldEnterEditMode && !isEditingGrades && authUser?.uid) {
      // Fetch grades directly from Firebase to ensure we have the latest data
      const fetchAndEnterEditMode = async () => {
        try {
          const gradesRef = ref(database, `user/${authUser.uid}/grades`);
          const gradesSnapshot = await get(gradesRef);
          
          if (gradesSnapshot.exists()) {
            const grades = gradesSnapshot.val();
            console.log('📚 Fetched grades directly from Firebase:', Object.keys(grades).length, 'courses');
            
            // Use a small delay to ensure the component has fully rendered
            setTimeout(() => {
              handleEditGrades();
              setShouldEnterEditMode(false);
            }, 200);
          } else {
            console.log('📚 No grades found in Firebase, waiting for UserContext update...');
            // If no grades in Firebase yet, wait a bit longer for UserContext to update
            const retryTimeout = setTimeout(() => {
              if (student?.grades && Object.keys(student.grades).length > 0) {
                handleEditGrades();
                setShouldEnterEditMode(false);
              } else {
                console.log('⚠️ Still no grades after retry, entering edit mode anyway');
                handleEditGrades(); // Enter edit mode even if no grades (user can add them)
                setShouldEnterEditMode(false);
              }
            }, 1000);
            return () => clearTimeout(retryTimeout);
          }
        } catch (error) {
          console.error('Error fetching grades for edit mode:', error);
          // If fetch fails, try using student.grades if available
          if (student?.grades && Object.keys(student.grades).length > 0) {
            setTimeout(() => {
              handleEditGrades();
              setShouldEnterEditMode(false);
            }, 200);
          } else {
            // Enter edit mode anyway (user can add courses)
            setTimeout(() => {
              handleEditGrades();
              setShouldEnterEditMode(false);
            }, 200);
          }
        }
      };
      
      fetchAndEnterEditMode();
    }
  }, [shouldEnterEditMode, isEditingGrades, authUser?.uid, student?.grades]);

  // Set avatar URL when component loads
  useEffect(() => {
    if (currentUser?.userIcon) {
      setAvatarUrl(currentUser.userIcon);
    }
  }, [currentUser?.userIcon]);

  // Load high schools on component mount
  useEffect(() => {
    const fetchHighSchools = async () => {
      setLoadingSchools(true);
      try {
        const schoolsRef = ref(database, 'highSchools');
        const snapshot = await get(schoolsRef);
        if (snapshot.exists()) {
          const schoolsData = snapshot.val();
          const schoolsList = Object.entries(schoolsData).map(([id, data]: [string, any]) => ({
            id,
            name: data.HighSchoolName || data.SchoolName || '',
            city: data.City || data.SchoolCity || data.HighSchoolCity || ''
          })).filter(school => school.name) // Filter out schools without names
          .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
          .filter((school, index, array) => {
            // Remove duplicates by keeping only the first occurrence of each school name
            return array.findIndex(s => s.name === school.name) === index;
          });
          setHighSchools(schoolsList);
        }
      } catch (error) {
        console.error('Error fetching high schools:', error);
      } finally {
        setLoadingSchools(false);
      }
    };

    fetchHighSchools();
  }, []);

  // Populate account form data when editing starts
  useEffect(() => {
    if (isEditingAccount && currentUser) {
      // Calculate grade from existing classYear
      let calculatedGrade = 12; // Default to Grade 12
      if (currentUser.classYear) {
        calculatedGrade = calculateGradeFromClassYear(currentUser.classYear);
      }
      
      setAccountFormData({
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        schoolName: currentUser.schoolName || '',
        schoolID: currentUser.schoolID || '',
        city: currentUser.city || '',
        province: currentUser.province || '',
        classYear: currentUser.classYear || '',
        grade: calculatedGrade, // Add calculated grade
        phoneNumber: currentUser.phoneNumber || '',
        email: currentUser.email || authUser?.email || '', // Fallback to Firebase Auth email
        country: currentUser.country || '',
        primaryLanguage: currentUser.primaryLanguage || '',
        status: currentUser.status || '',
        gender: currentUser.gender || '',
        accountType: currentUser.userType || 1, // Map userType to accountType
        isInternational: currentUser.isInternational || false,
        instagramUrl: currentUser.instagramUrl || '',
        twitterUrl: currentUser.twitterUrl || '',
        linkedinUrl: currentUser.linkedinUrl || ''
      });
    }
  }, [isEditingAccount, currentUser]);

  // Handle account form input changes
  const handleAccountInputChange = (field: string, value: string | boolean | number) => {
    setAccountFormData(prev => {
      const updatedData = {
        ...prev,
        [field]: value
      };
      
      // If grade is changed, automatically update classYear
      if (field === 'grade') {
        const gradeNum = typeof value === 'number' ? value : parseInt(value as string);
        if (gradeNum >= 9 && gradeNum <= 12) {
          updatedData.classYear = calculateClassYearFromGrade(gradeNum);
        }
      }
      
      return updatedData;
    });
    
    // Validate email and phone number on change
    if (field === 'email') {
      setEmailError(getEmailError(value as string));
    }
    if (field === 'phoneNumber') {
      setPhoneError(getPhoneError(value as string));
    }
  };

  // Handle high school selection
  const handleHighSchoolChange = (schoolName: string) => {
    // Find the selected school to get its ID
    const selectedSchool = highSchools.find(school => school.name === schoolName);
    
    console.log('🏫 High school selected:', {
      name: schoolName,
      id: selectedSchool?.id,
      selectedSchool
    });
    
    setAccountFormData(prev => ({
      ...prev,
      schoolName: schoolName,
      schoolID: selectedSchool?.id || ''
    }));
  };

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    // Check if it's a valid North American phone number (10 digits)
    // or international format (7-15 digits)
    return digitsOnly.length >= 7 && digitsOnly.length <= 15;
  };

  const getEmailError = (email: string): string => {
    if (!email) return '';
    if (!validateEmail(email)) return 'Please enter a valid email address';
    return '';
  };

  const getPhoneError = (phone: string): string => {
    if (!phone) return '';
    if (!validatePhoneNumber(phone)) return 'Please enter a valid phone number';
    return '';
  };

  // Handle saving account information
  const handleSaveAccount = async () => {
    if (!authUser?.uid) {
      setErrorMessage('User not authenticated');
      setShowErrorMessage(true);
      return;
    }

    // Validate email and phone before saving
    const emailValidationError = getEmailError(accountFormData.email);
    const phoneValidationError = getPhoneError(accountFormData.phoneNumber);
    
    setEmailError(emailValidationError);
    setPhoneError(phoneValidationError);
    
    if (emailValidationError || phoneValidationError) {
      setErrorMessage('Please fix validation errors before saving');
      setShowErrorMessage(true);
      return;
    }

    setIsSavingAccount(true);
    try {
      // Fetch existing user data to preserve all fields
      const userRef = ref(database, `user/${authUser.uid}`);
      const snapshot = await get(userRef);
      const existingUserData = snapshot.exists() ? snapshot.val() : {};
      const existingProfileData = existingUserData.ProfileData || {};
      
      // Merge existing ProfileData with new form data
      const updatedProfileData = {
        ...existingProfileData,
        'First Name': accountFormData.firstName,
        'Last Name': accountFormData.lastName,
        'High School': accountFormData.schoolName,
        hsID: accountFormData.schoolID,
        'Class Year': accountFormData.classYear,
        City: accountFormData.city,
        Province: accountFormData.province,
        'Phone Number': accountFormData.phoneNumber,
        Email: accountFormData.email,
        Country: accountFormData.country,
        'Primary Language': accountFormData.primaryLanguage,
        Status: accountFormData.status,
        gender: accountFormData.gender,
        isStudent: accountFormData.accountType, // Map accountType to isStudent field
        isInternational: accountFormData.isInternational,
        instagramUrl: accountFormData.instagramUrl,
        twitterUrl: accountFormData.twitterUrl,
        linkedinUrl: accountFormData.linkedinUrl,
      };
      
      // Update user profile in database
      const updates: { [key: string]: any } = {
        [`user/${authUser.uid}/ProfileData`]: updatedProfileData,
        [`user/${authUser.uid}/lastUpdated`]: Date.now()
      };

      await update(ref(database), updates);
      
      // Determine which fields were updated for logging
      const updatedFields: string[] = [];
      const fieldMappings = {
        'First Name': accountFormData.firstName,
        'Last Name': accountFormData.lastName,
        'High School': accountFormData.schoolName,
        'Class Year': accountFormData.classYear,
        'City': accountFormData.city,
        'Province': accountFormData.province,
        'Phone Number': accountFormData.phoneNumber,
        'Email': accountFormData.email,
        'Country': accountFormData.country,
        'Primary Language': accountFormData.primaryLanguage,
        'Status': accountFormData.status,
        'Gender': accountFormData.gender,
        'Account Type': accountFormData.accountType,
        'International Status': accountFormData.isInternational,
        'Instagram URL': accountFormData.instagramUrl,
        'Twitter URL': accountFormData.twitterUrl,
        'LinkedIn URL': accountFormData.linkedinUrl
      };
      
      // Compare each field with existing data
      Object.entries(fieldMappings).forEach(([fieldName, newValue]) => {
        const existingValue = existingProfileData[fieldName] || existingProfileData[fieldName.replace(' ', '')] || '';
        if (String(newValue) !== String(existingValue)) {
          updatedFields.push(fieldName);
        }
      });
      
      // Log the profile update activity
      if (updatedFields.length > 0) {
        try {
          await activityLogService.logProfileUpdate(authUser.uid, updatedFields.join(', '));
        } catch (error) {
          console.error('Error logging profile update activity:', error);
          // Don't block the flow if logging fails
        }
      }
      
      setSuccessMessage('Account information updated successfully!');
      setShowSuccessMessage(true);
      setIsEditingAccount(false);
      
      // Refresh user context if available
      // Note: You might need to add a refreshUser function to UserContext
    } catch (error) {
      console.error('Error saving account information:', error);
      setErrorMessage('Failed to save account information. Please try again.');
      setShowErrorMessage(true);
    } finally {
      setIsSavingAccount(false);
    }
  };

  // Handle canceling account editing
  const handleCancelAccount = () => {
    setIsEditingAccount(false);
    setAccountFormData({
      firstName: '',
      lastName: '',
      schoolName: '',
      schoolID: '',
      city: '',
      province: '',
      classYear: '',
      grade: 12, // Add grade field
      phoneNumber: '',
      email: '',
      country: '',
      primaryLanguage: '',
      status: '',
      gender: '',
      accountType: 1, // Default to Student
      isInternational: false,
      instagramUrl: '',
      twitterUrl: '',
      linkedinUrl: ''
    });
    setEmailError('');
    setPhoneError('');
  };

  // Avatar upload functions

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('File input changed');
    console.log('event.target.files:', event.target.files);
    
    const file = event.target.files?.[0];
    console.log('Selected file:', file);
    
    if (!file || !authUser?.uid) {
      console.log('No file selected or user not authenticated');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please select a valid image file');
      setShowErrorMessage(true);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Image size must be less than 5MB');
      setShowErrorMessage(true);
      return;
    }

    setIsUploadingAvatar(true);
    setErrorMessage('');

    try {
      // Delete old avatar if it exists
      if (avatarUrl) {
        try {
          const oldAvatarRef = storageRef(storage, avatarUrl);
          await deleteObject(oldAvatarRef);
        } catch (error) {
          console.log('No old avatar to delete or error deleting:', error);
        }
      }

      // Upload new avatar
      const avatarRef = storageRef(storage, `avatars/${authUser.uid}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(avatarRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Update user profile with new avatar URL
      const userRef = ref(database, `user/${authUser.uid}/ProfileData`);
      await update(userRef, {
        profileUrl: downloadURL,
        lastUpdated: Date.now()
      });

      setAvatarUrl(downloadURL);
      setSuccessMessage('Avatar updated successfully!');
      setShowSuccessMessage(true);
      
      // Refresh user context if available
      // Note: You might need to add a refreshUser function to UserContext
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setErrorMessage('Failed to upload avatar. Please try again.');
      setShowErrorMessage(true);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Get registered events from user's calendar data





  const handleEditGrades = () => {
    // Pre-populate the form fields with existing grades
    const existingGrade12Courses = transformGradesToDisplayFormat(studentToDisplay.grades).filter(c => {
      const yearStr = (c.year || '').toString().toLowerCase();
      return ['12', 'grade12', 'grade 12', '12th', 'gr12', 'g12'].includes(yearStr.replace(/\s+/g, ''));
    });
    
    const existingGrade11Courses = transformGradesToDisplayFormat(studentToDisplay.grades).filter(c => {
      const yearStr = (c.year || '').toString().toLowerCase();
      return ['11', 'grade11', 'grade 11', '11th', 'gr11', 'g11'].includes(yearStr.replace(/\s+/g, ''));
    });
    
    const existingGrade10Courses = transformGradesToDisplayFormat(studentToDisplay.grades).filter(c => {
      const yearStr = (c.year || '').toString().toLowerCase();
      return ['10', 'grade10', 'grade 10', '10th', 'gr10', 'g10'].includes(yearStr.replace(/\s+/g, ''));
    });

    // Create Grade 12 entries with existing data
    const populatedGrade12Entries: Course[] = existingGrade12Courses.map((course, index) => ({
      id: index + 1,
      name: course.name,
      grade: course.grade,
      gradeType: course.gradeType,
      courseName: course.courseName
    }));

    // Add default empty entries if needed (maintain at least 8 entries)
    while (populatedGrade12Entries.length < 8) {
      const defaultCourses = [
        { id: 1, name: 'ENG4U', grade: '', gradeType: 'actual' as const },
        { id: 2, name: 'MHF4U', grade: '', gradeType: 'actual' as const },
        { id: 3, name: 'MCV4U', grade: '', gradeType: 'actual' as const },
        { id: 4, name: 'MDM4U', grade: '', gradeType: 'actual' as const },
        { id: 5, name: 'SCH4U', grade: '', gradeType: 'actual' as const },
        { id: 6, name: 'SBI4U', grade: '', gradeType: 'actual' as const },
        { id: 23, name: '', grade: '', gradeType: 'actual' as const },
        { id: 24, name: '', grade: '', gradeType: 'actual' as const },
      ];
      const nextId = populatedGrade12Entries.length + 1;
      if (nextId <= 24) {
        populatedGrade12Entries.push({
          ...defaultCourses[nextId - 1],
          id: nextId
        });
      }
    }

    // Create Grade 11 entries with existing data
    const populatedGrade11Entries: Course[] = existingGrade11Courses.map((course, index) => ({
      id: index + 7, // Start from 7 to avoid conflicts with Grade 12
      name: course.name,
      grade: course.grade,
      gradeType: course.gradeType,
      courseName: course.courseName
    }));

    // Add default empty entries if needed (maintain at least 8 entries)
    while (populatedGrade11Entries.length < 8) {
      const defaultCourses = [
        { id: 7, name: 'ENG3U', grade: '', gradeType: 'actual' as const },
        { id: 8, name: 'MCR3U', grade: '', gradeType: 'actual' as const },
        { id: 9, name: 'SBI3U', grade: '', gradeType: 'actual' as const },
        { id: 18, name: 'SCH3U', grade: '', gradeType: 'actual' as const },
        { id: 19, name: 'SPH3U', grade: '', gradeType: 'actual' as const },
        { id: 20, name: 'HSP3U', grade: '', gradeType: 'actual' as const },
        { id: 21, name: 'CLU3M', grade: '', gradeType: 'actual' as const },
        { id: 22, name: 'FSF3U', grade: '', gradeType: 'actual' as const },
      ];
      const nextId = populatedGrade11Entries.length + 7;
      if (nextId <= 22) {
        populatedGrade11Entries.push({
          ...defaultCourses[nextId - 7],
          id: nextId
        });
      }
    }

    // Create Grade 10 entries with existing data
    const populatedGrade10Entries: Course[] = existingGrade10Courses.map((course, index) => ({
      id: index + 10, // Start from 10 to avoid conflicts with Grade 11 and 12
      name: course.name,
      grade: course.grade,
      gradeType: course.gradeType,
      courseName: course.courseName
    }));

    // Add default empty entries if needed (maintain at least 8 entries)
    while (populatedGrade10Entries.length < 8) {
      const defaultCourses = [
        { id: 10, name: 'ENG2D', grade: '', gradeType: 'actual' as const },
        { id: 11, name: 'MPM2D', grade: '', gradeType: 'actual' as const },
        { id: 12, name: 'SNC2D', grade: '', gradeType: 'actual' as const },
        { id: 13, name: 'CHC2D', grade: '', gradeType: 'actual' as const },
        { id: 14, name: 'CHV2O', grade: '', gradeType: 'actual' as const },
        { id: 15, name: 'GLC2O', grade: '', gradeType: 'actual' as const },
        { id: 16, name: 'FSF2O', grade: '', gradeType: 'actual' as const },
        { id: 17, name: 'BBI2O', grade: '', gradeType: 'actual' as const },
      ];
      const nextId = populatedGrade10Entries.length + 10;
      if (nextId <= 17) {
        populatedGrade10Entries.push({
          ...defaultCourses[nextId - 10],
          id: nextId
        });
      }
    }

    // Update the state with populated entries
    setGrade12CourseEntries(populatedGrade12Entries);
    setGrade11CourseEntries(populatedGrade11Entries);
    setGrade10CourseEntries(populatedGrade10Entries);
    setIsEditingGrades(true);
  };

  const handleCancelEditGrades = async () => {
    // If we have backup grades (from copying), restore them
    if (hasBackupGrades && authUser?.uid) {
      try {
        const backupRef = ref(database, `user/${authUser.uid}/grades_backup`);
        const backupSnapshot = await get(backupRef);
        
        if (backupSnapshot.exists()) {
          const originalGrades = backupSnapshot.val();
          const guidanceUserGradesRef = ref(database, `user/${authUser.uid}/grades`);
          
          // Restore original grades
          if (Object.keys(originalGrades).length > 0) {
            await set(guidanceUserGradesRef, originalGrades);
          } else {
            // If original was empty, remove grades
            await remove(guidanceUserGradesRef);
          }
          
          // Remove backup
          await remove(backupRef);
          setHasBackupGrades(false);
          
          setSuccessMessage('Changes cancelled. Original courses and grades restored.');
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 3000);
        }
      } catch (error) {
        console.error('Error restoring backup grades:', error);
        setErrorMessage('Failed to restore original courses and grades.');
        setShowErrorMessage(true);
      }
    }
    
    // Reset the form fields back to default state
    setGrade12CourseEntries([
      { id: 1, name: 'ENG4U', grade: '', gradeType: 'actual' },
      { id: 2, name: 'MHF4U', grade: '', gradeType: 'actual' },
      { id: 3, name: 'MCV4U', grade: '', gradeType: 'actual' },
      { id: 4, name: 'MDM4U', grade: '', gradeType: 'actual' },
      { id: 5, name: 'SCH4U', grade: '', gradeType: 'actual' },
      { id: 6, name: 'SBI4U', grade: '', gradeType: 'actual' },
      { id: 23, name: '', grade: '', gradeType: 'actual' },
      { id: 24, name: '', grade: '', gradeType: 'actual' },
    ]);
    setGrade11CourseEntries([
      { id: 7, name: 'ENG3U', grade: '', gradeType: 'actual' },
      { id: 8, name: 'MCR3U', grade: '', gradeType: 'actual' },
      { id: 9, name: 'SBI3U', grade: '', gradeType: 'actual' },
      { id: 18, name: 'SCH3U', grade: '', gradeType: 'actual' },
      { id: 19, name: 'SPH3U', grade: '', gradeType: 'actual' },
      { id: 20, name: 'HSP3U', grade: '', gradeType: 'actual' },
      { id: 21, name: 'CLU3M', grade: '', gradeType: 'actual' },
      { id: 22, name: 'FSF3U', grade: '', gradeType: 'actual' },
    ]);
    setGrade10CourseEntries([
      { id: 10, name: 'ENG2D', grade: '', gradeType: 'actual' },
      { id: 11, name: 'MPM2D', grade: '', gradeType: 'actual' },
      { id: 12, name: 'SNC2D', grade: '', gradeType: 'actual' },
      { id: 13, name: 'CHC2D', grade: '', gradeType: 'actual' },
      { id: 14, name: 'CHV2O', grade: '', gradeType: 'actual' },
      { id: 15, name: 'GLC2O', grade: '', gradeType: 'actual' },
      { id: 16, name: 'FSF2O', grade: '', gradeType: 'actual' },
      { id: 17, name: 'BBI2O', grade: '', gradeType: 'actual' },
    ]);
    setIsEditingGrades(false);
  };


  // Function to find school ID by name
  const findSchoolIdByName = async (schoolName: string): Promise<string | null> => {
    try {
      const schoolsRef = ref(database, 'schools');
      const snapshot = await get(schoolsRef);
      if (snapshot.exists()) {
        const schoolsData = snapshot.val();
        for (const schoolId in schoolsData) {
          const schoolData = schoolsData[schoolId];
          if (schoolData['SchoolName'] === schoolName) {
            return schoolId;
          }
        }
      }
      return null;
    } catch (error) {
      console.error('Error finding school ID:', error);
      return null;
    }
  };

  // Function to handle school chip click
  const handleSchoolClick = async (schoolName: string) => {
    const schoolId = await findSchoolIdByName(schoolName);
    if (schoolId) {
      navigate(`/schools/${schoolId}`);
    } else {
      console.error('School not found:', schoolName);
    }
  };

  // Function to find program ID by name
  const findProgramIdByName = async (programName: string): Promise<string | null> => {
    try {
      const programsRef = ref(database, 'programs');
      const snapshot = await get(programsRef);
      if (snapshot.exists()) {
        const programsData = snapshot.val();
        for (const programId in programsData) {
          const programData = programsData[programId];
          if (programData['ProgramName'] === programName) {
            return programId;
          }
        }
      }
      return null;
    } catch (error) {
      console.error('Error finding program ID:', error);
      return null;
    }
  };

  // Function to handle program chip click
  const handleProgramClick = async (programName: string) => {
    const programId = await findProgramIdByName(programName);
    if (programId) {
      navigate(`/programs/${programId}`);
    } else {
      console.error('Program not found:', programName);
    }
  };

  // Helper to get school name from report (handles both schoolName and SchoolName)
  const getSchoolName = (report: any) => report.schoolName || report.SchoolName || '';

  // Function to handle adding a new activity
  const handleAddActivity = async () => {
    if (!authUser?.uid) {
      setErrorMessage('No user data available');
      setShowErrorMessage(true);
      return;
    }

    if (!newActivity.interest.trim()) {
      setErrorMessage('Please enter an activity name');
      setShowErrorMessage(true);
      return;
    }

    setIsSaving(true);
    try {
      // First, ensure the user record exists with student data
      if (!student) {
        console.log('Creating new user record with student data for user:', authUser.uid);
        
        // First, check if user data already exists to preserve it
        const userRef = ref(database, `user/${authUser.uid}`);
        const snapshot = await get(userRef);
        const existingUserData = snapshot.exists() ? snapshot.val() : {};
        
        // Merge existing data with new student data
        const userData = {
          ...existingUserData,
          ProfileData: {
            ...(existingUserData.ProfileData || {}),
            'First Name': studentToDisplay.firstName || existingUserData.ProfileData?.['First Name'] || '',
            'Last Name': studentToDisplay.lastName || existingUserData.ProfileData?.['Last Name'] || '',
            'High School': studentToDisplay.school || existingUserData.ProfileData?.['High School'] || '',
            'City': studentToDisplay.city || existingUserData.ProfileData?.['City'] || '',
            'Province': studentToDisplay.province || existingUserData.ProfileData?.['Province'] || '',
            'Class Year': studentToDisplay.grade || existingUserData.ProfileData?.['Class Year'] || '',
            'Status': studentToDisplay.status || existingUserData.ProfileData?.['Status'] || 'Active',
            'Email': existingUserData.ProfileData?.['Email'] || authUser?.email || ''
          },
          // Preserve existing profileExtra or create new one
          profileExtra: {
            ...(existingUserData.profileExtra || {}),
            interests_ActivitiesProfile: existingUserData.profileExtra?.interests_ActivitiesProfile || {}
          }
        };
        
        await set(userRef, userData);
        console.log('User record with student data created successfully, preserving existing data');
      }

      const activityId = `activity_${Date.now()}`;
      const userRef = ref(database, `user/${authUser.uid}/profileExtra/interests_ActivitiesProfile/${activityId}`);
      
      console.log('Adding activity with path:', `user/${authUser.uid}/profileExtra/interests_ActivitiesProfile/${activityId}`);
      console.log('Activity data:', {
        interest: newActivity.interest.trim(),
        startGrade: newActivity.startGrade,
        endGrade: newActivity.endGrade,
        description: newActivity.description,
        link: newActivity.link
      });
      
      await set(userRef, {
        interest: newActivity.interest.trim(),
        startGrade: newActivity.startGrade,
        endGrade: newActivity.endGrade,
        description: newActivity.description,
        link: newActivity.link
      });

      // Reset form and close dialog
      setNewActivity({
        interest: '',
        startGrade: '',
        endGrade: '',
        description: '',
        link: ''
      });
      setShowAddActivity(false);
      
      // UserContext handles real-time updates automatically
      setSuccessMessage('Activity added successfully!');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error adding activity:', error);
      console.error('Error details:', {
        authUser: authUser?.uid,
        student: student?.id,
        newActivity: newActivity,
        error: error
      });
      setErrorMessage(`Failed to add activity: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setShowErrorMessage(true);
      // Close the modal even if there's an error
      setShowAddActivity(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to handle removing an activity
  const handleRemoveActivity = async (activityId: string) => {
    if (!authUser?.uid) {
      setErrorMessage('No user data available');
      setShowErrorMessage(true);
      return;
    }

    setIsSaving(true);
    try {
      const userRef = ref(database, `user/${authUser.uid}/profileExtra/interests_ActivitiesProfile/${activityId}`);
      await remove(userRef);

      // UserContext handles real-time updates automatically
      setSuccessMessage('Activity removed successfully!');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error removing activity:', error);
      setErrorMessage('Failed to remove activity. Please try again.');
      setShowErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to handle adding a new work/volunteering activity
  const handleAddWorkVolunteer = async () => {
    if (!authUser?.uid) {
      setErrorMessage('No user data available');
      setShowErrorMessage(true);
      return;
    }

    if (!newWorkVolunteer.position.trim()) {
      setErrorMessage('Please enter a position title');
      setShowErrorMessage(true);
      return;
    }

    setIsSaving(true);
    try {
      // First, ensure the user record exists with student data
      if (!student) {
        console.log('Creating new user record with student data for user:', authUser.uid);
        
        // First, check if user data already exists to preserve it
        const userRef = ref(database, `user/${authUser.uid}`);
        const snapshot = await get(userRef);
        const existingUserData = snapshot.exists() ? snapshot.val() : {};
        
        // Merge existing data with new student data
        const userData = {
          ...existingUserData,
          ProfileData: {
            ...(existingUserData.ProfileData || {}),
            'First Name': studentToDisplay.firstName || existingUserData.ProfileData?.['First Name'] || '',
            'Last Name': studentToDisplay.lastName || existingUserData.ProfileData?.['Last Name'] || '',
            'High School': studentToDisplay.school || existingUserData.ProfileData?.['High School'] || '',
            'City': studentToDisplay.city || existingUserData.ProfileData?.['City'] || '',
            'Province': studentToDisplay.province || existingUserData.ProfileData?.['Province'] || '',
            'Class Year': studentToDisplay.grade || existingUserData.ProfileData?.['Class Year'] || '',
            'Status': studentToDisplay.status || existingUserData.ProfileData?.['Status'] || 'Active',
            'Email': existingUserData.ProfileData?.['Email'] || authUser?.email || ''
          },
          // Preserve existing profileExtra or create new one
          profileExtra: {
            ...(existingUserData.profileExtra || {}),
            work_VolunteerExpProfile: existingUserData.profileExtra?.work_VolunteerExpProfile || {}
          }
        };
        
        await set(userRef, userData);
        console.log('User record with student data created successfully, preserving existing data');
      }

      const workVolunteerId = `workVolunteer_${Date.now()}`;
      const userRef = ref(database, `user/${authUser.uid}/profileExtra/work_VolunteerExpProfile/${workVolunteerId}`);
      
      console.log('Adding work/volunteering activity with path:', `user/${authUser.uid}/profileExtra/work_VolunteerExpProfile/${workVolunteerId}`);
      console.log('Work/Volunteering data:', {
        position: newWorkVolunteer.position.trim(),
        employer: newWorkVolunteer.employer,
        startGrade: newWorkVolunteer.startGrade,
        endGrade: newWorkVolunteer.endGrade,
        description: newWorkVolunteer.description
      });
      
      await set(userRef, {
        position: newWorkVolunteer.position.trim(),
        employer: newWorkVolunteer.employer,
        startDate: newWorkVolunteer.startGrade,
        endDate: newWorkVolunteer.endGrade,
        description: newWorkVolunteer.description
      });

      // Reset form and close dialog
      setNewWorkVolunteer({
        position: '',
        employer: '',
        startGrade: '',
        endGrade: '',
        description: ''
      });
      setShowAddWorkVolunteer(false);
      
      // UserContext handles real-time updates automatically
      setSuccessMessage('Work/Volunteering activity added successfully!');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error adding work/volunteering activity:', error);
      console.error('Error details:', {
        authUser: authUser?.uid,
        student: student?.id,
        newWorkVolunteer: newWorkVolunteer,
        error: error
      });
      setErrorMessage(`Failed to add work/volunteering activity: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setShowErrorMessage(true);
      // Close the modal even if there's an error
      setShowAddWorkVolunteer(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to handle removing a work/volunteering activity
  const handleRemoveWorkVolunteer = async (workVolunteerId: string) => {
    if (!authUser?.uid) {
      setErrorMessage('No user data available');
      setShowErrorMessage(true);
      return;
    }

    setIsSaving(true);
    try {
      const userRef = ref(database, `user/${authUser.uid}/profileExtra/work_VolunteerExpProfile/${workVolunteerId}`);
      await remove(userRef);

      // UserContext handles real-time updates automatically
      setSuccessMessage('Work/Volunteering activity removed successfully!');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error removing work/volunteering activity:', error);
      setErrorMessage('Failed to remove work/volunteering activity. Please try again.');
      setShowErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to show delete confirmation
  const handleDeleteClick = (activityId: string, activityName: string) => {
    setActivityToDelete({ id: activityId, name: activityName });
    setShowDeleteConfirmation(true);
  };

  // Function to confirm deletion
  const handleConfirmDelete = async () => {
    if (activityToDelete) {
      await handleRemoveActivity(activityToDelete.id);
      setShowDeleteConfirmation(false);
      setActivityToDelete(null);
    }
  };

  // Function to cancel deletion
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setActivityToDelete(null);
  };

  // Function to show delete confirmation for work/volunteering
  const handleWorkVolunteerDeleteClick = (workVolunteerId: string, workVolunteerName: string) => {
    setWorkVolunteerToDelete({ id: workVolunteerId, name: workVolunteerName });
    setShowDeleteWorkVolunteerConfirmation(true);
  };

  // Function to confirm delete for work/volunteering
  const handleWorkVolunteerConfirmDelete = async () => {
    if (workVolunteerToDelete) {
      await handleRemoveWorkVolunteer(workVolunteerToDelete.id);
      setShowDeleteWorkVolunteerConfirmation(false);
      setWorkVolunteerToDelete(null);
    }
  };

  // Function to cancel delete for work/volunteering
  const handleWorkVolunteerCancelDelete = () => {
    setShowDeleteWorkVolunteerConfirmation(false);
    setWorkVolunteerToDelete(null);
  };

  // Function to handle adding a new athletics activity
  const handleAddAthletics = async () => {
    if (!authUser?.uid) {
      setErrorMessage('No user data available');
      setShowErrorMessage(true);
      return;
    }

    if (!newAthletics.sport.trim()) {
      setErrorMessage('Please enter a sport name');
      setShowErrorMessage(true);
      return;
    }

    setIsSaving(true);
    try {
      // First, ensure the user record exists with student data
      if (!student) {
        console.log('Creating new user record with student data for user:', authUser.uid);
        
        // First, check if user data already exists to preserve it
        const userRef = ref(database, `user/${authUser.uid}`);
        const snapshot = await get(userRef);
        const existingUserData = snapshot.exists() ? snapshot.val() : {};
        
        // Merge existing data with new student data
        const userData = {
          ...existingUserData,
          ProfileData: {
            ...(existingUserData.ProfileData || {}),
            'First Name': studentToDisplay.firstName || existingUserData.ProfileData?.['First Name'] || '',
            'Last Name': studentToDisplay.lastName || existingUserData.ProfileData?.['Last Name'] || '',
            'High School': studentToDisplay.school || existingUserData.ProfileData?.['High School'] || '',
            'City': studentToDisplay.city || existingUserData.ProfileData?.['City'] || '',
            'Province': studentToDisplay.province || existingUserData.ProfileData?.['Province'] || '',
            'Class Year': studentToDisplay.grade || existingUserData.ProfileData?.['Class Year'] || '',
            'Status': studentToDisplay.status || existingUserData.ProfileData?.['Status'] || 'Active',
            'Email': existingUserData.ProfileData?.['Email'] || authUser?.email || ''
          },
          // Preserve existing profileExtra or create new one
          profileExtra: {
            ...(existingUserData.profileExtra || {}),
            athleticsSportsProfile: existingUserData.profileExtra?.athleticsSportsProfile || {}
          }
        };
        
        await set(userRef, userData);
        console.log('User record with student data created successfully, preserving existing data');
      }

      const athleticsId = newAthletics.sport.trim().replace(/\s+/g, '_');
      const userRef = ref(database, `user/${authUser.uid}/profileExtra/athleticsSportsProfile/${athleticsId}`);
      
      console.log('Adding athletics activity with path:', `user/${authUser.uid}/profileExtra/athleticsSportsProfile/${athleticsId}`);
      console.log('Athletics data:', {
        position: newAthletics.position,
        startGrade: newAthletics.startGrade,
        endGrade: newAthletics.endGrade,
        description: newAthletics.description,
        jerseyNumber: newAthletics.jerseyNumber,
        level: newAthletics.level,
        youtubeLink: newAthletics.youtubeLink,
        articleLink: newAthletics.articleLink,
        isContinue: newAthletics.isContinue ? 1 : 0
      });
      
      await set(userRef, {
        position: newAthletics.position,
        startGrade: newAthletics.startGrade,
        endGrade: newAthletics.endGrade,
        description: newAthletics.description,
        jerseyNumber: newAthletics.jerseyNumber,
        level: newAthletics.level,
        youtubeLink: newAthletics.youtubeLink,
        articleLink: newAthletics.articleLink,
        isContinue: newAthletics.isContinue ? 1 : 0
      });

      // Reset form and close dialog
      setNewAthletics({
        sport: '',
        position: '',
        startGrade: '',
        endGrade: '',
        description: '',
        jerseyNumber: '',
        level: '',
        youtubeLink: '',
        articleLink: '',
        isContinue: false
      });
      setShowAddAthletics(false);
      
      // UserContext handles real-time updates automatically
      setSuccessMessage('Athletics activity added successfully!');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error adding athletics activity:', error);
      console.error('Error details:', {
        authUser: authUser?.uid,
        student: student?.id,
        newAthletics: newAthletics,
        error: error
      });
      setErrorMessage(`Failed to add athletics activity: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setShowErrorMessage(true);
      // Close the modal even if there's an error
      setShowAddAthletics(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to handle removing an athletics activity
  const handleRemoveAthletics = async (athleticsId: string) => {
    if (!authUser?.uid) {
      setErrorMessage('No user data available');
      setShowErrorMessage(true);
      return;
    }

    setIsSaving(true);
    try {
      const userRef = ref(database, `user/${authUser.uid}/profileExtra/athleticsSportsProfile/${athleticsId}`);
      await remove(userRef);

      // UserContext handles real-time updates automatically
      setSuccessMessage('Athletics activity removed successfully!');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error removing athletics activity:', error);
      setErrorMessage('Failed to remove athletics activity. Please try again.');
      setShowErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to show delete confirmation for athletics
  const handleAthleticsDeleteClick = (athleticsId: string, athleticsName: string) => {
    setAthleticsToDelete({ id: athleticsId, name: athleticsName });
    setShowDeleteAthleticsConfirmation(true);
  };

  // Function to confirm delete for athletics
  const handleAthleticsConfirmDelete = async () => {
    if (athleticsToDelete) {
      await handleRemoveAthletics(athleticsToDelete.id);
      setShowDeleteAthleticsConfirmation(false);
      setAthleticsToDelete(null);
    }
  };

  // Function to cancel delete for athletics
  const handleAthleticsCancelDelete = () => {
    setShowDeleteAthleticsConfirmation(false);
    setAthleticsToDelete(null);
  };

  // Function to handle adding a new academic award/honour
  const handleAddAwardsHonours = async () => {
    if (!authUser?.uid) {
      setErrorMessage('No user data available');
      setShowErrorMessage(true);
      return;
    }

    if (!newAwardsHonours.title.trim()) {
      setErrorMessage(`Please enter a ${getAwardHonourSingular()} title`);
      setShowErrorMessage(true);
      return;
    }

    setIsSaving(true);
    try {
      // First, ensure the user record exists with student data
      if (!student) {
        console.log('Creating new user record with student data for user:', authUser.uid);
        
        // First, check if user data already exists to preserve it
        const userRef = ref(database, `user/${authUser.uid}`);
        const snapshot = await get(userRef);
        const existingUserData = snapshot.exists() ? snapshot.val() : {};
        
        // Merge existing data with new student data
        const userData = {
          ...existingUserData,
          ProfileData: {
            ...(existingUserData.ProfileData || {}),
            'First Name': studentToDisplay.firstName || existingUserData.ProfileData?.['First Name'] || '',
            'Last Name': studentToDisplay.lastName || existingUserData.ProfileData?.['Last Name'] || '',
            'High School': studentToDisplay.school || existingUserData.ProfileData?.['High School'] || '',
            'City': studentToDisplay.city || existingUserData.ProfileData?.['City'] || '',
            'Province': studentToDisplay.province || existingUserData.ProfileData?.['Province'] || '',
            'Class Year': studentToDisplay.grade || existingUserData.ProfileData?.['Class Year'] || '',
            'Status': studentToDisplay.status || existingUserData.ProfileData?.['Status'] || 'Active',
            'Email': existingUserData.ProfileData?.['Email'] || authUser?.email || ''
          },
          // Preserve existing profileExtra or create new one
          profileExtra: {
            ...(existingUserData.profileExtra || {}),
            academicAwardsHonoursProfile: existingUserData.profileExtra?.academicAwardsHonoursProfile || {}
          }
        };
        
        await set(userRef, userData);
        console.log('User record with student data created successfully, preserving existing data');
      }

      const awardId = newAwardsHonours.title.trim().replace(/\s+/g, '_');
      const userRef = ref(database, `user/${authUser.uid}/profileExtra/academicAwardsHonoursProfile/${awardId}`);
      
      console.log(`Adding academic ${getAwardHonourSingular()} with path:`, `user/${authUser.uid}/profileExtra/academicAwardsHonoursProfile/${awardId}`);
      console.log('Award data:', {
        title: newAwardsHonours.title,
        grade: newAwardsHonours.grade,
        yearReceived: newAwardsHonours.yearReceived,
        description: newAwardsHonours.description
      });
      
      await set(userRef, {
        title: newAwardsHonours.title,
        grade: newAwardsHonours.grade,
        yearReceived: newAwardsHonours.yearReceived,
        description: newAwardsHonours.description
      });

      // Reset form and close dialog
      setNewAwardsHonours({
        title: '',
        grade: '',
        yearReceived: '',
        description: ''
      });
      setShowAddAwardsHonours(false);
      
      // UserContext handles real-time updates automatically
      setSuccessMessage(`Academic ${getAwardHonourSingular()} added successfully!`);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error(`Error adding academic ${getAwardHonourSingular()}:`, error);
      console.error('Error details:', {
        authUser: authUser?.uid,
        student: student?.id,
        newAwardsHonours: newAwardsHonours,
        error: error
      });
      setErrorMessage(`Failed to add academic ${getAwardHonourSingular()}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setShowErrorMessage(true);
      // Close the modal even if there's an error
      setShowAddAwardsHonours(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to handle removing an academic award/honour
  const handleRemoveAwardsHonours = async (awardId: string) => {
    if (!authUser?.uid) {
      setErrorMessage('No user data available');
      setShowErrorMessage(true);
      return;
    }

    setIsSaving(true);
    try {
      const userRef = ref(database, `user/${authUser.uid}/profileExtra/academicAwardsHonoursProfile/${awardId}`);
      await remove(userRef);

      // UserContext handles real-time updates automatically
      setSuccessMessage(`Academic ${getAwardHonourSingular()} removed successfully!`);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error(`Error removing academic ${getAwardHonourSingular()}:`, error);
      setErrorMessage(`Failed to remove academic ${getAwardHonourSingular()}. Please try again.`);
      setShowErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to show delete confirmation for academic awards/honours
  const handleAwardsHonoursDeleteClick = (awardId: string, awardName: string) => {
    setAwardsHonoursToDelete({ id: awardId, name: awardName });
    setShowDeleteAwardsHonoursConfirmation(true);
  };

  // Function to confirm delete for academic awards/honours
  const handleAwardsHonoursConfirmDelete = async () => {
    if (awardsHonoursToDelete) {
      await handleRemoveAwardsHonours(awardsHonoursToDelete.id);
      setShowDeleteAwardsHonoursConfirmation(false);
      setAwardsHonoursToDelete(null);
    }
  };

  // Function to cancel delete for academic awards/honours
  const handleAwardsHonoursCancelDelete = () => {
    setShowDeleteAwardsHonoursConfirmation(false);
    setAwardsHonoursToDelete(null);
  };

  // Function to handle adding a new academic extenuating circumstance
  const handleAddExtenuatingCircumstances = async () => {
    if (!authUser?.uid) {
      setErrorMessage('No user data available');
      setShowErrorMessage(true);
      return;
    }

    if (!newExtenuatingCircumstances.description.trim()) {
      setErrorMessage('Please enter a description of the extenuating circumstance');
      setShowErrorMessage(true);
      return;
    }

    setIsSaving(true);
    try {
      // First, ensure the user record exists with student data
      if (!student) {
        console.log('Creating new user record with student data for user:', authUser.uid);
        
        // First, check if user data already exists to preserve it
        const userRef = ref(database, `user/${authUser.uid}`);
        const snapshot = await get(userRef);
        const existingUserData = snapshot.exists() ? snapshot.val() : {};
        
        // Merge existing data with new student data
        const userData = {
          ...existingUserData,
          ProfileData: {
            ...(existingUserData.ProfileData || {}),
            'First Name': studentToDisplay.firstName || existingUserData.ProfileData?.['First Name'] || '',
            'Last Name': studentToDisplay.lastName || existingUserData.ProfileData?.['Last Name'] || '',
            'High School': studentToDisplay.school || existingUserData.ProfileData?.['High School'] || '',
            'City': studentToDisplay.city || existingUserData.ProfileData?.['City'] || '',
            'Province': studentToDisplay.province || existingUserData.ProfileData?.['Province'] || '',
            'Class Year': studentToDisplay.grade || existingUserData.ProfileData?.['Class Year'] || '',
            'Status': studentToDisplay.status || existingUserData.ProfileData?.['Status'] || 'Active',
            'Email': existingUserData.ProfileData?.['Email'] || authUser?.email || ''
          },
          // Preserve existing profileExtra or create new one
          profileExtra: {
            ...(existingUserData.profileExtra || {}),
            academicExtenuatingCircumstancesProfile: existingUserData.profileExtra?.academicExtenuatingCircumstancesProfile || {}
          }
        };
        
        await set(userRef, userData);
        console.log('User record with student data created successfully, preserving existing data');
      }

      const circumstanceId = newExtenuatingCircumstances.description.trim().replace(/\s+/g, '_').substring(0, 20);
      const userRef = ref(database, `user/${authUser.uid}/profileExtra/academicExtenuatingCircumstancesProfile/${circumstanceId}`);
      
      console.log('Adding academic extenuating circumstance with path:', `user/${authUser.uid}/profileExtra/academicExtenuatingCircumstancesProfile/${circumstanceId}`);
      console.log('Circumstance data:', {
        description: newExtenuatingCircumstances.description
      });
      
      await set(userRef, {
        description: newExtenuatingCircumstances.description
      });

      // Reset form and close dialog
      setNewExtenuatingCircumstances({
        description: ''
      });
      setShowAddExtenuatingCircumstances(false);
      
      // UserContext handles real-time updates automatically
      setSuccessMessage('Academic extenuating circumstance added successfully!');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error adding academic extenuating circumstance:', error);
      console.error('Error details:', {
        authUser: authUser?.uid,
        student: student?.id,
        newExtenuatingCircumstances: newExtenuatingCircumstances,
        error: error
      });
      setErrorMessage(`Failed to add academic extenuating circumstance: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setShowErrorMessage(true);
      // Close the modal even if there's an error
      setShowAddExtenuatingCircumstances(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to handle removing an academic extenuating circumstance
  const handleRemoveExtenuatingCircumstances = async (circumstanceId: string) => {
    if (!authUser?.uid) {
      setErrorMessage('No user data available');
      setShowErrorMessage(true);
      return;
    }

    setIsSaving(true);
    try {
      const userRef = ref(database, `user/${authUser.uid}/profileExtra/academicExtenuatingCircumstancesProfile/${circumstanceId}`);
      await remove(userRef);

      // UserContext handles real-time updates automatically
      setSuccessMessage('Academic extenuating circumstance removed successfully!');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error removing academic extenuating circumstance:', error);
      setErrorMessage('Failed to remove academic extenuating circumstance. Please try again.');
      setShowErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to show delete confirmation for academic extenuating circumstances
  const handleExtenuatingCircumstancesDeleteClick = (circumstanceId: string, circumstanceName: string) => {
    setExtenuatingCircumstancesToDelete({ id: circumstanceId, name: circumstanceName });
    setShowDeleteExtenuatingCircumstancesConfirmation(true);
  };

  // Function to confirm delete for academic extenuating circumstances
  const handleExtenuatingCircumstancesConfirmDelete = async () => {
    if (extenuatingCircumstancesToDelete) {
      await handleRemoveExtenuatingCircumstances(extenuatingCircumstancesToDelete.id);
      setShowDeleteExtenuatingCircumstancesConfirmation(false);
      setExtenuatingCircumstancesToDelete(null);
    }
  };

  // Function to cancel delete for academic extenuating circumstances
  const handleExtenuatingCircumstancesCancelDelete = () => {
    setShowDeleteExtenuatingCircumstancesConfirmation(false);
    setExtenuatingCircumstancesToDelete(null);
  };

  // Academics section handlers
  const handleAddGrade12Course = () => {
    setGrade12CourseEntries([...grade12CourseEntries, { id: Date.now(), name: '', grade: '', gradeType: 'projected' }]);
  };

  const handleRemoveGrade12Course = (id: number) => {
    setGrade12CourseEntries(grade12CourseEntries.filter((course) => course.id !== id));
  };

  const handleGrade12CourseChange = (id: number, field: 'name' | 'grade' | 'gradeType', value: string) => {
    setGrade12CourseEntries(
      grade12CourseEntries.map((course) =>
        course.id === id ? { ...course, [field]: value } : course
      )
    );
  };

  const handleAddGrade11Course = () => {
    setGrade11CourseEntries([...grade11CourseEntries, { id: Date.now(), name: '', grade: '', gradeType: 'projected' }]);
  };

  const handleRemoveGrade11Course = (id: number) => {
    setGrade11CourseEntries(grade11CourseEntries.filter((course) => course.id !== id));
  };

  const handleGrade11CourseChange = (id: number, field: 'name' | 'grade' | 'gradeType', value: string) => {
    setGrade11CourseEntries(
      grade11CourseEntries.map((course) =>
        course.id === id ? { ...course, [field]: value } : course
      )
    );
  };

  const handleAddGrade10Course = () => {
    setGrade10CourseEntries([...grade10CourseEntries, { id: Date.now(), name: '', grade: '', gradeType: 'projected' }]);
  };

  const handleRemoveGrade10Course = (id: number) => {
    setGrade10CourseEntries(grade10CourseEntries.filter((course) => course.id !== id));
  };

  const handleGrade10CourseChange = (id: number, field: 'name' | 'grade' | 'gradeType', value: string) => {
    setGrade10CourseEntries(
      grade10CourseEntries.map((course) =>
        course.id === id ? { ...course, [field]: value } : course
      )
    );
  };

  // Function to fetch courses from Firebase
  const fetchCoursesFromFirebase = async () => {
    if (!authUser?.uid) return;

    try {
      setCoursesLoading(true);
      const coursesRef = ref(database, 'highSchoolCourses_new');
      const snapshot = await get(coursesRef);
      
      if (snapshot.exists()) {
        const coursesData = snapshot.val();
        const grade12Courses: CourseOption[] = [];
        const grade11Courses: CourseOption[] = [];
        const grade10Courses: CourseOption[] = [];

        // Iterate through all courses
        for (const courseCode in coursesData) {
          const courseData = coursesData[courseCode] as FirebaseCourse;
          const grade = courseData.Grade;
          const name = courseData.Name || '';

          if (grade === '12') {
            grade12Courses.push({
              code: courseCode,
              description: name
            });
          } else if (grade === '11') {
            grade11Courses.push({
              code: courseCode,
              description: name
            });
          } else if (grade === '10') {
            grade10Courses.push({
              code: courseCode,
              description: name
            });
          }
        }

        // Sort courses by code for consistency
        grade12Courses.sort((a, b) => a.code.localeCompare(b.code));
        grade11Courses.sort((a, b) => a.code.localeCompare(b.code));
        grade10Courses.sort((a, b) => a.code.localeCompare(b.code));

        setGrade12CourseOptions(grade12Courses);
        setGrade11CourseOptions(grade11Courses);
        setGrade10CourseOptions(grade10Courses);
      } else {
        console.log('No courses found in Firebase');
        setGrade12CourseOptions([]);
        setGrade11CourseOptions([]);
        setGrade10CourseOptions([]);
      }
    } catch (error) {
      console.error('Error fetching courses from Firebase:', error);
      setErrorMessage('Failed to load courses from database');
      setShowErrorMessage(true);
    } finally {
      setCoursesLoading(false);
    }
  };

  const handleSaveCourseGrades = async () => {
    console.log('🔄 SAVE BUTTON CLICKED - handleSaveCourseGrades called!');
    console.log('🔄 authUser?.uid:', authUser?.uid);
    if (!authUser?.uid) return;

    // Auto-scroll to top of page so user can see the progress indicator
    console.log('🔄 Scrolling to top of page...');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Also scroll the document element to ensure we get to the very top
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    try {
      setCoursesLoading(true);
      
      // Collect all course entries with grades
      const allCourseEntries = [
        ...grade12CourseEntries.filter(course => course.name && course.grade),
        ...grade11CourseEntries.filter(course => course.name && course.grade),
        ...grade10CourseEntries.filter(course => course.name && course.grade)
      ];

      if (allCourseEntries.length === 0) {
        setErrorMessage('Please enter at least one course with a grade');
        setShowErrorMessage(true);
        return;
      }

      // Fetch course details from highSchoolCourses_new
      const coursesRef = ref(database, 'highSchoolCourses_new');
      const coursesSnapshot = await get(coursesRef);
      
      if (!coursesSnapshot.exists()) {
        setErrorMessage('Failed to load course information from database');
        setShowErrorMessage(true);
        return;
      }

      const coursesData = coursesSnapshot.val();
      const gradesToSave: { [key: string]: any } = {};

      // Process each course entry
      for (const entry of allCourseEntries) {
        const courseCode = entry.name;
        const courseData = coursesData[courseCode];
        
        if (courseData) {
          // Determine the grade level based on the course code
          let gradeLevel = '';
          if (courseCode.includes('4U') || courseCode.includes('4M') || courseCode.includes('4C') || courseCode.includes('4E')) {
            gradeLevel = '12';
          } else if (courseCode.includes('3U') || courseCode.includes('3M') || courseCode.includes('3C') || courseCode.includes('3E')) {
            gradeLevel = '11';
          } else if (courseCode.includes('2D') || courseCode.includes('2P') || courseCode.includes('2L')) {
            gradeLevel = '10';
          } else {
            // Fallback to course data grade if available
            gradeLevel = courseData.Grade || '';
          }
          
          gradesToSave[courseCode] = {
            Prerequisite: courseData.Prerequisite || "None",
            courseType: courseData.CourseType || "Open",
            grade: gradeLevel, // Use determined grade level
            gradeType: entry.gradeType,
            grades: entry.grade, // Store without % symbol
            level: courseData.Level || "",
            name: courseData.Name || courseData.name || "", // Try both Name and name fields
            segment: courseData.Segment || ""
          };
        }
      }

      // Check if grades have actually changed before saving
      const currentGradesRef = ref(database, `user/${authUser.uid}/grades`);
      const currentGradesSnapshot = await get(currentGradesRef);
      const currentGrades = currentGradesSnapshot.exists() ? currentGradesSnapshot.val() : {};
      
      // Compare current grades with new grades
      const currentGradesHash = JSON.stringify(currentGrades);
      const newGradesHash = JSON.stringify(gradesToSave);
      
      if (currentGradesHash === newGradesHash) {
        console.log('⏸️ No grade changes detected, skipping save and recalculation');
        setSuccessMessage('No changes to save');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        return;
      }
      
      console.log('🔄 Grade changes detected, proceeding with save and recalculation');
      
      // Save to user's grades object
      const userGradesRef = ref(database, `user/${authUser.uid}/grades`);
      console.log('💾 Saving grades to Firebase:', gradesToSave);
      await set(userGradesRef, gradesToSave);
      console.log('✅ Grades saved successfully');

      // If we had backup grades (from copying), remove the backup since we're saving
      if (hasBackupGrades) {
        try {
          const backupRef = ref(database, `user/${authUser.uid}/grades_backup`);
          await remove(backupRef);
          setHasBackupGrades(false);
          console.log('✅ Backup grades removed after save');
        } catch (error) {
          console.error('Error removing backup grades:', error);
          // Don't fail the save if backup removal fails
        }
      }

      // Log the grade update activity
      try {
        console.log('🔄 Starting activity logging...');
        const courseCount = Object.keys(gradesToSave).length;
        console.log('🔄 Course count:', courseCount);
        console.log('🔄 About to call activityLogService.logGradeUpdate...');
        await activityLogService.logGradeUpdate(authUser.uid, courseCount);
        console.log('✅ Activity logging completed successfully');
      } catch (error) {
        console.error('❌ Error logging grade update activity:', error);
        if (error instanceof Error) {
          console.error('❌ Error details:', error.message);
          console.error('❌ Error stack:', error.stack);
        } else {
          console.error('❌ Error details:', String(error));
        }
        console.log('🔄 Continuing despite activity logging error...');
        // Don't block the flow if logging fails
      }
      
      console.log('🔄 Activity logging section completed, continuing to next step...');
      console.log('🔄 About to start progress bar logic...');

      // Force a small delay to ensure Firebase has processed the update
      console.log('🔄 Waiting 500ms for Firebase to process update...');
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('✅ Firebase delay completed, proceeding to eligibility recalculation...');

      // Trigger eligibility recalculation after grade updates
      try {
        console.log('🔄 Starting eligibility recalculation section...');
        console.log('🔄 Clearing old eligibility data before recalculation...');
        clearEligibility();
        console.log('🔄 clearEligibility() completed successfully');
        
        // Check if user needs migration and migrate automatically if needed
        try {
          const isMigrated = await eligibilityMigrationService.checkAndMigrateUserIfNeeded(authUser.uid);
          if (isMigrated) {
            console.log('✅ User is now migrated, will use API-based calculation');
          } else {
            console.log('📊 User not migrated, will use local calculation');
          }
        } catch (error) {
          console.error('❌ Error in automatic migration check:', error);
          // Continue with local calculation as fallback
        }
        
        console.log('🔄 Triggering Firebase API eligibility recalculation after grade update...');
        console.log('🔄 Setting isRecalculatingEligibility to true');
        setIsRecalculatingEligibility(true);
        console.log('🔄 Setting recalculationProgress to Starting eligibility recalculation...');
        setRecalculationProgress('Starting eligibility recalculation...');
        console.log('🔄 Setting successMessage and showSuccessMessage for starting recalculation');
        setSuccessMessage('Starting eligibility recalculation...');
        setShowSuccessMessage(true);
        
        // Force UI update by triggering a re-render
        console.log('🔄 Forcing UI update for progress bar...');
        // Use a small delay to ensure state updates are processed
        await new Promise(resolve => setTimeout(resolve, 50));
        console.log('🔄 UI update delay completed, progress bar should now be visible');
        
        // Set up progress indicators for recalculation
        console.log('🔄 Setting up progress indicators for recalculation');
        setRecalculationProgress('Preparing calculation...');
        setSuccessMessage('Preparing calculation...');
        setShowSuccessMessage(true);
        
        // Ensure progress bar is rendered before scrolling
        console.log('🔄 Waiting for progress bar to render...');
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Scroll to top again to ensure progress bar is visible
        console.log('🔄 Scrolling to top again to show progress bar...');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        
        // Show preparation message for a reasonable time (5 seconds)
        console.log('🔄 Showing preparation message for 5 seconds...');
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        // Call Firebase API to recalculate eligibility for all programs
        try {
          // Show calculation message and keep it visible during API call
          console.log('🔄 Updating to calculation phase...');
          setRecalculationProgress('Calculating program eligibility...');
          setSuccessMessage('Calculating program eligibility...');
          setShowSuccessMessage(true);
          
          const refreshResult = await eligibilityRefreshService.startRefresh();
          
          if (refreshResult.success) {
            console.log('✅ Firebase API eligibility recalculation completed successfully');
            console.log('📊 Student Profile: Eligibility data will be updated from Firebase cache');
            setRecalculationProgress('Eligibility recalculation completed successfully!');
            setSuccessMessage('Eligibility calculation completed!');
            setShowSuccessMessage(true);
          } else {
            console.warn('⚠️ Firebase API eligibility recalculation failed:', refreshResult.error);
            console.log('📊 Student Profile: Will use existing Firebase cache data');
            setRecalculationProgress('Eligibility recalculation completed with warnings.');
            setSuccessMessage('Eligibility calculation completed with warnings.');
            setShowSuccessMessage(true);
          }
        } catch (error) {
          console.error('❌ Error calling Firebase API for eligibility recalculation:', error);
          console.log('📊 Student Profile: Will use existing Firebase cache data');
          setRecalculationProgress('Eligibility recalculation completed with errors.');
          setSuccessMessage('Eligibility calculation completed with errors.');
          setShowSuccessMessage(true);
        } finally {
          setIsRecalculatingEligibility(false);
          
          // Show green completion message at top for 2 seconds
          console.log('🔄 Showing green completion message at top for 2 seconds...');
          setShowCompletionAtTop(true);
          await new Promise(resolve => setTimeout(resolve, 2000));
          setShowCompletionAtTop(false);
          
          // Clear progress message after completion delay
          setTimeout(() => {
            setRecalculationProgress('');
          }, 2000);
          
          // Auto-hide bottom progress bar after completion
          setTimeout(() => {
            setShowSuccessMessage(false);
            setSuccessMessage('');
          }, 5000);
        }
      } catch (error) {
        console.error('❌ Error triggering eligibility recalculation:', error);
        // Don't block the success flow if recalculation fails
      }
      
      // Clear the course entry forms and reset to default state
      setGrade12CourseEntries([
        { id: 1, name: 'ENG4U', grade: '', gradeType: 'projected' },
        { id: 2, name: 'MHF4U', grade: '', gradeType: 'projected' },
        { id: 3, name: 'MCV4U', grade: '', gradeType: 'projected' },
        { id: 4, name: 'MDM4U', grade: '', gradeType: 'projected' },
        { id: 5, name: 'SCH4U', grade: '', gradeType: 'projected' },
        { id: 6, name: 'SBI4U', grade: '', gradeType: 'projected' },
        { id: 23, name: '', grade: '', gradeType: 'projected' },
        { id: 24, name: '', grade: '', gradeType: 'projected' },
      ]);
      setGrade11CourseEntries([
        { id: 7, name: 'ENG3U', grade: '', gradeType: 'projected' },
        { id: 8, name: 'MCR3U', grade: '', gradeType: 'projected' },
        { id: 9, name: 'SBI3U', grade: '', gradeType: 'projected' },
        { id: 18, name: 'SCH3U', grade: '', gradeType: 'projected' },
        { id: 19, name: 'SPH3U', grade: '', gradeType: 'projected' },
        { id: 20, name: 'HSP3U', grade: '', gradeType: 'projected' },
        { id: 21, name: 'CLU3M', grade: '', gradeType: 'projected' },
        { id: 22, name: 'FSF3U', grade: '', gradeType: 'projected' },
      ]);
      setGrade10CourseEntries([
        { id: 10, name: 'ENG2D', grade: '', gradeType: 'projected' },
        { id: 11, name: 'MPM2D', grade: '', gradeType: 'projected' },
        { id: 12, name: 'SNC2D', grade: '', gradeType: 'projected' },
        { id: 13, name: 'CHC2D', grade: '', gradeType: 'projected' },
        { id: 14, name: 'CHV2O', grade: '', gradeType: 'projected' },
        { id: 15, name: 'GLC2O', grade: '', gradeType: 'projected' },
        { id: 16, name: 'FSF2O', grade: '', gradeType: 'projected' },
        { id: 17, name: 'BBI2O', grade: '', gradeType: 'projected' },
      ]);
      
      // Exit edit mode
      setIsEditingGrades(false);

    } catch (error) {
      console.error('Error saving course grades:', error);
      setErrorMessage('Failed to save course grades');
      setShowErrorMessage(true);
    } finally {
      setCoursesLoading(false);
    }
  };

  // Transform grades data from the new structure to display format
  const transformGradesToDisplayFormat = (grades: any): DisplayCourse[] => {
    if (!grades || typeof grades !== 'object') return [];
    
    console.log('🔄 Transforming grades for display:', grades);
    
    return Object.entries(grades).map(([courseCode, courseData]: [string, any]): DisplayCourse => {
      console.log(`Processing course ${courseCode} for display:`, courseData);
      
      // Try to find the course name from the course options
      let courseName = courseCode; // Default to course code
      
      // Look up in Grade 12 courses first
      const grade12Course = grade12CourseOptions.find(opt => opt.code === courseCode);
      if (grade12Course) {
        courseName = grade12Course.description;
      } else {
        // Look up in Grade 11 courses
        const grade11Course = grade11CourseOptions.find(opt => opt.code === courseCode);
        if (grade11Course) {
          courseName = grade11Course.description;
        } else {
          // Look up in Grade 10 courses
          const grade10Course = grade10CourseOptions.find(opt => opt.code === courseCode);
          if (grade10Course) {
            courseName = grade10Course.description;
          } else {
            // Fallback to courseData.name if available, otherwise use course code
            courseName = courseData.name || courseCode;
          }
        }
      }
      
      const displayCourse = {
        name: courseCode,
        courseName: courseName,
        grade: courseData.grades || '',
        gradeType: courseData.gradeType || 'actual',
        year: courseData.grade || '' // Use the Grade field from highSchoolCourses_new
      };
      
      console.log(`✅ Created display course for ${courseCode}:`, displayCourse);
      return displayCourse;
    });
  };

  // Use useMemo to recalculate grades when student data changes
  const grade12Courses = useMemo(() => {
    console.log('🔄 Recalculating grade12Courses with grades:', studentToDisplay.grades);
    const transformed = transformGradesToDisplayFormat(studentToDisplay.grades);
    const filtered = transformed.filter(c => {
      const yearStr = (c.year || '').toString().toLowerCase();
      const isGrade12 = [
        '12', 'grade12', 'grade 12', '12th', 'gr12', 'g12'
      ].includes(yearStr.replace(/\s+/g, ''));
      console.log(`Course ${c.name} year "${c.year}" isGrade12: ${isGrade12}`);
      return isGrade12;
    });
    console.log('✅ Final grade12Courses:', filtered);
    return filtered;
  }, [studentToDisplay.grades, grade12CourseOptions, grade11CourseOptions, grade10CourseOptions]);

  const grade11Courses = useMemo(() => {
    return transformGradesToDisplayFormat(studentToDisplay.grades).filter(c => {
      const yearStr = (c.year || '').toString().toLowerCase();
      return [
        '11', 'grade11', 'grade 11', '11th', 'gr11', 'g11'
      ].includes(yearStr.replace(/\s+/g, ''));
    });
  }, [studentToDisplay.grades, grade12CourseOptions, grade11CourseOptions, grade10CourseOptions]);

  const grade10Courses = useMemo(() => {
    return transformGradesToDisplayFormat(studentToDisplay.grades).filter(c => {
      const yearStr = (c.year || '').toString().toLowerCase();
      return [
        '10', 'grade10', 'grade 10', '10th', 'gr10', 'g10'
      ].includes(yearStr.replace(/\s+/g, ''));
    });
  }, [studentToDisplay.grades, grade12CourseOptions, grade11CourseOptions, grade10CourseOptions]);

  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (userError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{userError}</Alert>
      </Box>
    );
  }

  // Debug: Log progress bar state
  console.log('🔄 Progress bar render - isRecalculatingEligibility:', isRecalculatingEligibility, 'recalculationProgress:', recalculationProgress);

  return (
    <Box sx={{ width: '100%', mt: 4, px: { xs: 1, sm: 2, md: 4 } }}>
      {isNewProfile && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Welcome! This is your new student profile. You can add your information through the various sections below.
        </Alert>
      )}
      <Paper sx={{ p: 4 }}>
        {/* Header Progress Indicator */}
        {(isRecalculatingEligibility || showCompletionAtTop) && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            backgroundColor: showCompletionAtTop ? '#4caf50' : '#1976d2', 
            color: 'white', 
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            ...(isRecalculatingEligibility && {
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 0.8 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.8 }
              }
            })
          }}>
            {isRecalculatingEligibility && <CircularProgress size={24} sx={{ color: 'white' }} />}
            <Typography variant="body1" sx={{ fontWeight: 'bold', flex: 1 }}>
              {successMessage || 'Processing...'}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box sx={{ position: 'relative', mr: 3 }}>
            <Avatar 
              src={avatarUrl || studentToDisplay.profileImage || authUser?.userIcon || currentUser?.userIcon || undefined} 
              alt={studentToDisplay.firstName + ' ' + studentToDisplay.lastName} 
              sx={{ 
                width: 96, 
                height: 96
              }}
            />
            <IconButton
              component="label"
              disabled={isUploadingAvatar}
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                backgroundColor: 'primary.main',
                color: 'white',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&:disabled': {
                  backgroundColor: 'grey.400',
                }
              }}
            >
              {isUploadingAvatar ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CameraAltIcon sx={{ fontSize: 16 }} />
              )}
              <input
                type="file"
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
                accept="image/*"
                multiple={false}
              />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
              {studentToDisplay.firstName || 'New Student'} {studentToDisplay.lastName}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0, sm: 4 } }}>
              <Box>
                <Typography variant="subtitle1" color="text.secondary">
                  {studentToDisplay.school || 'No school listed'}
                </Typography>
                {studentToDisplay.city && studentToDisplay.province && (
                  <Typography variant="subtitle1" color="text.secondary">
                    {`${studentToDisplay.city}, ${studentToDisplay.province}`}
                  </Typography>
                )}
                <Typography variant="subtitle1" color="text.secondary">
                  Grade: {studentToDisplay.grade ? calculateGradeFromClassYear(studentToDisplay.grade) : 'Not specified'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                {studentToDisplay.profileCompletion && (
                  <Box sx={{ width: 96, height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', ml: 6, mt: -5 }}>
                    <CircularProgressbar
                      value={Number(studentToDisplay.profileCompletion)}
                      text={`${studentToDisplay.profileCompletion}%`}
                      styles={buildStyles({
                        pathColor: '#8dd439',
                        textColor: '#222',
                        trailColor: '#e6e6e6',
                        textSize: '20px',
                        strokeLinecap: 'round',
                      })}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mb: 3 }} />
        {/* Tabs */}
        <Tabs 
          value={visibleTabs.includes(selectedTab) ? selectedTab : visibleTabs[0] || 0}
          onChange={(_, newValue) => {
            setSelectedTab(newValue);
            // Update URL parameters based on selected tab
            const tabNames = ['academics', 'athletics', 'work', 'clubs', 'account'];
            const updatedSearchParams = new URLSearchParams(searchParams);
            updatedSearchParams.set('tab', tabNames[newValue]);
            setSearchParams(updatedSearchParams);
          }} 
          sx={{ mb: 3 }}
        >
          <Tab 
            label={isGuidanceUser && isViewingOwnProfile ? "Courses & Grades" : "Academics"} 
            sx={{ display: visibleTabs.includes(0) ? 'flex' : 'none' }}
          />
          <Tab 
            label="Athletics" 
            sx={{ display: visibleTabs.includes(1) ? 'flex' : 'none' }}
          />
          <Tab 
            label="Work & Volunteering" 
            sx={{ display: visibleTabs.includes(2) ? 'flex' : 'none' }}
          />
          <Tab 
            label="Clubs & Activities" 
            sx={{ display: visibleTabs.includes(3) ? 'flex' : 'none' }}
          />
          <Tab 
            label="Account" 
            sx={{ display: visibleTabs.includes(4) ? 'flex' : 'none' }}
          />
        </Tabs>

        {/* Academics Tab */}
        {selectedTab === 0 && (
          <Box>
            {/* Existing Courses Display */}
            {!isEditingGrades && (
              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 0, mr: 1 }}>Courses and Grades</Typography>
                  {(grade12Courses.length > 0 || grade11Courses.length > 0 || grade10Courses.length > 0) ? (
                    <Tooltip title="Edit Courses">
                      <IconButton onClick={handleEditGrades} color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Add Courses">
                      <IconButton onClick={handleEditGrades} color="primary">
                        <AddCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {(grade12Courses.length > 0 || grade11Courses.length > 0 || grade10Courses.length > 0) && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 3,
                    }}
                  >
                  {grade12Courses.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>Grade 12 Courses</Typography>
                      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                        <Table size="small" sx={{ fontSize: 14 }}>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'grey.100' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>Course Code</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Course Name</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actual Grade</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Projected Grade</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {grade12Courses.map((course, idx) => (
                              <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50' }}>
                                <TableCell>{course.name}</TableCell>
                                <TableCell>{course.courseName || ''}</TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                  {course.gradeType === 'actual' && course.grade ? `${course.grade}%` : ''}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                  {course.gradeType === 'projected' && course.grade ? `${course.grade}%` : ''}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                  
                  {grade11Courses.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>Grade 11 Courses</Typography>
                      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                        <Table size="small" sx={{ fontSize: 14 }}>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'grey.100' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>Course Code</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Course Name</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actual Grade</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Projected Grade</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {grade11Courses.map((course, idx) => (
                              <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50' }}>
                                <TableCell>{course.name}</TableCell>
                                <TableCell>{course.courseName || ''}</TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                  {course.gradeType === 'actual' && course.grade ? `${course.grade}%` : ''}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                  {course.gradeType === 'projected' && course.grade ? `${course.grade}%` : ''}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                  
                  {grade10Courses.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>Grade 10 Courses</Typography>
                      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                        <Table size="small" sx={{ fontSize: 14 }}>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'grey.100' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>Course Code</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Course Name</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actual Grade</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Projected Grade</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {grade10Courses.map((course, idx) => (
                              <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50' }}>
                                <TableCell>{course.name}</TableCell>
                                <TableCell>{course.courseName || ''}</TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                  {course.gradeType === 'actual' && course.grade ? `${course.grade}%` : ''}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                  {course.gradeType === 'projected' && course.grade ? `${course.grade}%` : ''}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Box>
                  )}
              </Box>
            )}



            {/* Grade Entry Section - Based on Eligibility Report Layout */}
            {isEditingGrades && (
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} lg={6}>
                  {/* Grade 12 Courses */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" sx={{ mr: 1 }}>
                        Enter Grade 12 Courses
                      </Typography>
                      <Tooltip title="Add Course">
                        <IconButton onClick={handleAddGrade12Course} color="primary">
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      To calculate your projected admissible programs, you need to tell us your real (or anticipated) grades in the Grade 12 courses you've completed or expect to complete. If you have more than 6 Grade 12 courses, click the + sign to add more rows for more courses.
                    </Typography>

                    {/* Column Headers */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, px: 1 }}>
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 200, maxWidth: 300, fontWeight: 'bold' }}>
                        Course
                      </Typography>
                      <Typography variant="body2" sx={{ width: 120, fontWeight: 'bold' }}>
                        Grade Type
                      </Typography>
                      <Typography variant="body2" sx={{ width: 100, fontWeight: 'bold' }}>
                        Grade
                      </Typography>
                    </Box>

                    {grade12CourseEntries.map((course, index) => (
                      <Box key={course.id} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                        <Autocomplete
                          size="small"
                          sx={{ flex: 1, minWidth: 200, maxWidth: 300 }}
                          options={grade12CourseOptions}
                          getOptionLabel={(option) => `${option.code} - ${option.description}`}
                          value={grade12CourseOptions.find(opt => opt.code === course.name) || null}
                          onChange={(event, newValue) => {
                            handleGrade12CourseChange(course.id, 'name', newValue?.code || '');
                          }}
                          disabled={coursesLoading}
                          loading={coursesLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Type to search for a course..."
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {coursesLoading ? 'Loading...' : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {option.code}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.description}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options;
                            
                            const filtered = options.filter(option =>
                              option.description.toLowerCase().includes(inputValue.toLowerCase()) ||
                              option.code.toLowerCase().includes(inputValue.toLowerCase())
                            );
                            
                            return filtered;
                          }}
                          isOptionEqualToValue={(option, value) => option.code === value?.code}
                          noOptionsText="No courses found"
                          clearOnEscape
                          selectOnFocus
                          handleHomeEndKeys
                        />
                        <Select
                          value={course.gradeType}
                          onChange={(e) => handleGrade12CourseChange(course.id, 'gradeType', e.target.value)}
                          sx={{ width: 120 }}
                        >
                          <MenuItem value="actual">Actual</MenuItem>
                          <MenuItem value="projected">Projected</MenuItem>
                        </Select>
                        <TextField
                          type="number"
                          label="Grade"
                          value={course.grade}
                          onChange={(e) => handleGrade12CourseChange(course.id, 'grade', e.target.value)}
                          InputProps={{
                            endAdornment: '%',
                          }}
                          sx={{ width: 100 }}
                        />
                        {grade12CourseEntries.length > 1 && (
                          <Tooltip title="Remove Course">
                            <IconButton onClick={() => handleRemoveGrade12Course(course.id)} color="primary">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Grid>
                
                <Grid item xs={12} lg={6}>
                  {/* Grade 11 and Grade 10 Courses */}
                  <Box>
                    {/* Grade 11 Courses */}
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" sx={{ mr: 1 }}>
                          Enter Grade 11 Courses
                        </Typography>
                        <Tooltip title="Add Course">
                          <IconButton onClick={handleAddGrade11Course} color="primary">
                            <AddCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter your real (or anticipated) grade from Grade 11 English (ENG3U). This course is a prerequisite for some programs. Optionally, you can also add other Grade 11 courses, click the + sign to add more rows for more courses.
                      </Typography>

                      {/* Column Headers */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, px: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 200, maxWidth: 300, fontWeight: 'bold' }}>
                          Course
                        </Typography>
                        <Typography variant="body2" sx={{ width: 120, fontWeight: 'bold' }}>
                          Grade Type
                        </Typography>
                        <Typography variant="body2" sx={{ width: 100, fontWeight: 'bold' }}>
                          Grade
                        </Typography>
                      </Box>

                      {grade11CourseEntries.map((course, index) => (
                        <Box key={course.id} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                          <Autocomplete
                            size="small"
                            sx={{ flex: 1, minWidth: 200, maxWidth: 300 }}
                            options={grade11CourseOptions}
                            getOptionLabel={(option) => `${option.code} - ${option.description}`}
                            value={grade11CourseOptions.find(opt => opt.code === course.name) || null}
                            onChange={(event, newValue) => {
                              handleGrade11CourseChange(course.id, 'name', newValue?.code || '');
                            }}
                            disabled={coursesLoading}
                            loading={coursesLoading}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Type to search for a course..."
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {coursesLoading ? 'Loading...' : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <Box component="li" {...props}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {option.code}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.description}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            filterOptions={(options, { inputValue }) => {
                              if (!inputValue) return options;
                              
                              const filtered = options.filter(option =>
                                option.description.toLowerCase().includes(inputValue.toLowerCase()) ||
                                option.code.toLowerCase().includes(inputValue.toLowerCase())
                              );
                              
                              return filtered;
                            }}
                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                            noOptionsText="No courses found"
                            clearOnEscape
                            selectOnFocus
                            handleHomeEndKeys
                          />
                          <Select
                            value={course.gradeType}
                            onChange={(e) => handleGrade11CourseChange(course.id, 'gradeType', e.target.value)}
                            sx={{ width: 120 }}
                          >
                            <MenuItem value="actual">Actual</MenuItem>
                            <MenuItem value="projected">Projected</MenuItem>
                          </Select>
                          <TextField
                            type="number"
                            label="Grade"
                            value={course.grade}
                            onChange={(e) => handleGrade11CourseChange(course.id, 'grade', e.target.value)}
                            InputProps={{
                              endAdornment: '%',
                            }}
                            sx={{ width: 100 }}
                          />
                          {grade11CourseEntries.length > 1 && (
                            <Tooltip title="Remove Course">
                              <IconButton onClick={() => handleRemoveGrade11Course(course.id)} color="primary">
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      ))}
                    </Box>

                    {/* Grade 10 Courses */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" sx={{ mr: 1 }}>
                          Enter Grade 10 Courses
                        </Typography>
                        <Tooltip title="Add Course">
                          <IconButton onClick={handleAddGrade10Course} color="primary">
                            <AddCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Column Headers */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, px: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 200, maxWidth: 300, fontWeight: 'bold' }}>
                          Course
                        </Typography>
                        <Typography variant="body2" sx={{ width: 120, fontWeight: 'bold' }}>
                          Grade Type
                        </Typography>
                        <Typography variant="body2" sx={{ width: 100, fontWeight: 'bold' }}>
                          Grade
                        </Typography>
                      </Box>

                      {grade10CourseEntries.map((course, index) => (
                        <Box key={course.id} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                          <Autocomplete
                            size="small"
                            sx={{ flex: 1, minWidth: 200, maxWidth: 300 }}
                            options={grade10CourseOptions}
                            getOptionLabel={(option) => `${option.code} - ${option.description}`}
                            value={grade10CourseOptions.find(opt => opt.code === course.name) || null}
                            onChange={(event, newValue) => {
                              handleGrade10CourseChange(course.id, 'name', newValue?.code || '');
                            }}
                            disabled={coursesLoading}
                            loading={coursesLoading}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Type to search for a course..."
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {coursesLoading ? 'Loading...' : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <Box component="li" {...props}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {option.code}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.description}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            filterOptions={(options, { inputValue }) => {
                              if (!inputValue) return options;
                              
                              const filtered = options.filter(option =>
                                option.description.toLowerCase().includes(inputValue.toLowerCase()) ||
                                option.code.toLowerCase().includes(inputValue.toLowerCase())
                              );
                              
                              return filtered;
                            }}
                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                            noOptionsText="No courses found"
                            clearOnEscape
                            selectOnFocus
                            handleHomeEndKeys
                          />
                          <Select
                            value={course.gradeType}
                            onChange={(e) => handleGrade10CourseChange(course.id, 'gradeType', e.target.value)}
                            sx={{ width: 120 }}
                          >
                            <MenuItem value="actual">Actual</MenuItem>
                            <MenuItem value="projected">Projected</MenuItem>
                          </Select>
                          <TextField
                            type="number"
                            label="Grade"
                            value={course.grade}
                            onChange={(e) => handleGrade10CourseChange(course.id, 'grade', e.target.value)}
                            InputProps={{
                              endAdornment: '%',
                            }}
                            sx={{ width: 100 }}
                          />
                          {grade10CourseEntries.length > 1 && (
                            <Tooltip title="Remove Course">
                              <IconButton onClick={() => handleRemoveGrade10Course(course.id)} color="primary">
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}

            {/* Save and Cancel Buttons */}
            {isEditingGrades && (() => {
              console.log('🔄 SAVE BUTTON RENDERED - isEditingGrades:', isEditingGrades);
              return (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  onClick={(e) => {
                    console.log('🔄 SAVE BUTTON CLICKED - onClick triggered!');
                    console.log('🔄 Event object:', e);
                    console.log('🔄 coursesLoading:', coursesLoading);
                    console.log('🔄 isRecalculatingEligibility:', isRecalculatingEligibility);
                    console.log('🔄 Button disabled:', coursesLoading || isRecalculatingEligibility);
                    console.log('🔄 Calling handleSaveCourseGrades...');
                    handleSaveCourseGrades();
                  }}
                  disabled={coursesLoading || isRecalculatingEligibility}
                  sx={{ minWidth: 120 }}
                >
                  {coursesLoading ? 'Saving...' : isRecalculatingEligibility ? 'Recalculating Eligibility...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleCancelEditGrades}
                  sx={{ minWidth: 120 }}
                >
                  Cancel
                </Button>
              </Box>
              );
            })()}
            
            {/* Academic Information */}
            <Box sx={{ mb: 4, mt: 4 }}>
              <Typography variant="h6" gutterBottom>Academic Information</Typography>
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ minWidth: 170 }}>
                    Grade 12 Average:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {studentToDisplay.grade12Average !== undefined ? `${studentToDisplay.grade12Average.toFixed(1)}%` : 'Not available'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ minWidth: 170 }}>
                    Top 6 Average:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {studentToDisplay.grade12Top6Average !== undefined ? `${studentToDisplay.grade12Top6Average.toFixed(1)}%` : 'Not available'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Awards and Honours - Hide for guidance users viewing own profile */}
            {!(isGuidanceUser && isViewingOwnProfile) && (
            <Box sx={{ mb: 4, mt: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 0, mr: 1 }}>{getAwardsHonoursLabel()}</Typography>
                <Tooltip title={`Add ${getAwardHonourLabel()}`}>
                  <IconButton onClick={() => setShowAddAwardsHonours(true)} color="primary">
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              {studentToDisplay.academicAwardsHonoursProfile && Object.keys(studentToDisplay.academicAwardsHonoursProfile).length > 0 ? (
                <TableContainer component={Paper} sx={{ boxShadow: 'none', mt: 2, width: '100%' }}>
                  <Table size="small" sx={{ fontSize: 14, width: '100%' }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: 14, width: '80%' }}>{getAwardHonourLabel()}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: 14, width: '20%', textAlign: 'center' }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(studentToDisplay.academicAwardsHonoursProfile).map(([key, data], idx) => (
                        <TableRow key={key} sx={{ backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50' }}>
                          <TableCell sx={{ py: 1, px: 2, width: '80%' }}>
                            {data.title || key}
                            {data.grade ? `, grade ${data.grade}` : ''}
                            {data.yearReceived ? ` in ${data.yearReceived}` : ''}
                            {data.description && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                {data.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1, px: 2, width: '20%', textAlign: 'center' }}>
                            <Tooltip title="Remove">
                              <IconButton 
                                onClick={() => handleAwardsHonoursDeleteClick(key, data.title || key)}
                                disabled={isSaving}
                                color="primary"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No {getAwardsHonoursLabel()} to display.
                </Typography>
              )}
            </Box>
            )}
            
            {/* Extenuating Circumstances - Hide for guidance users viewing own profile */}
            {!(isGuidanceUser && isViewingOwnProfile) && (
            <Box sx={{ mb: 4, mt: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 0, mr: 1 }}>Extenuating Circumstances</Typography>
                <Tooltip title="Add Circumstance">
                  <IconButton onClick={() => setShowAddExtenuatingCircumstances(true)} color="primary">
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              {studentToDisplay.academicExtenuatingCircumstancesProfile && Object.keys(studentToDisplay.academicExtenuatingCircumstancesProfile).length > 0 ? (
                <TableContainer component={Paper} sx={{ boxShadow: 'none', mt: 2, width: '100%' }}>
                  <Table size="small" sx={{ fontSize: 14, width: '100%' }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: 14, width: '80%' }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: 14, width: '20%', textAlign: 'center' }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(studentToDisplay.academicExtenuatingCircumstancesProfile).map(([key, data], idx) => (
                        <TableRow key={key} sx={{ backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50' }}>
                          <TableCell sx={{ py: 1, px: 2, width: '80%' }}>
                            {data.description || key}
                          </TableCell>
                          <TableCell sx={{ py: 1, px: 2, width: '20%', textAlign: 'center' }}>
                            <Tooltip title="Remove">
                              <IconButton 
                                onClick={() => handleExtenuatingCircumstancesDeleteClick(key, data.description || key)}
                                disabled={isSaving}
                                color="primary"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No extenuating circumstances to display.
                </Typography>
              )}
            </Box>
            )}
          </Box>
        )}

        {/* Athletics Tab */}
        {selectedTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0, mr: 1 }}>Sports</Typography>
              <Tooltip title="Add Sport">
                <IconButton onClick={() => setShowAddAthletics(true)} color="primary">
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
            {studentToDisplay.athleticsSportsProfile && Object.keys(studentToDisplay.athleticsSportsProfile).length > 0 && (
              <TableContainer component={Paper} sx={{ boxShadow: 'none', mt: 2, width: '100%' }}>
                <Table size="small" sx={{ fontSize: 14, tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 150, width: 150 }}>Sport</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 80, width: 80 }}>Position</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 60, width: 60 }}>Grades</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 80, width: 80 }}>Level</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 60, width: 60 }}>Jersey #</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 120, width: 120 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 100, width: 100 }}>Links</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 80, width: 80 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(studentToDisplay.athleticsSportsProfile).map(([key, data], idx) => (
                      <TableRow key={key} sx={{ backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50' }}>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 150, width: 150 }}>{key}</TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 80, width: 80 }}>{data.position || ''}</TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 60, width: 60, textAlign: 'center' }}>
                          {(() => {
                            const start = data.startGrade;
                            const end = data.endGrade;
                            if (!start) return '';
                            if (!end || start === end) return start;
                            if (end > start) return `${start}-${end}`;
                            return start;
                          })()}
                        </TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 80, width: 80 }}>{data.level || ''}</TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 60, width: 60, textAlign: 'center' }}>{data.jerseyNumber || ''}</TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 120, width: 120 }}>{data.description || ''}</TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 100, width: 100 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {data.youtubeLink && (
                              <a href={data.youtubeLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>YouTube</a>
                            )}
                            {data.articleLink && (
                              <a href={data.articleLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>Article</a>
                            )}
                            {(data.isContinue === 1 || data.isContinue === '1') && (
                              <Typography variant="caption" color="primary" sx={{ fontSize: '11px' }}>
                                Interested in playing in school
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 80, width: 80, textAlign: 'right' }}>
                          <Tooltip title="Remove">
                            <IconButton 
                              onClick={() => handleAthleticsDeleteClick(key, key)}
                              disabled={isSaving}
                              color="primary"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Awards and Honours Section */}
            {studentToDisplay.athleticsAwardsProfile && Object.keys(studentToDisplay.athleticsAwardsProfile).length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>{getAwardsHonoursLabel()}</Typography>
                <TableContainer component={Paper} sx={{ boxShadow: 'none', mt: 2, width: '100%' }}>
                  <Table size="small" sx={{ fontSize: 14, tableLayout: 'fixed', width: '100%' }}>
                    <TableBody>
                      {Object.entries(studentToDisplay.athleticsAwardsProfile).map(([sport, data], idx) => (
                        <TableRow key={sport} sx={{ backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50' }}>
                          <TableCell sx={{ py: 1, px: 2, minWidth: 220, width: 220 }}>{data.title ? `${data.title}, ` : ''}{data.sport || sport}</TableCell>
                          <TableCell sx={{ py: 1, px: 2, minWidth: 180, width: 180 }}>
                            {(data.startDate && data.endDate) ? `${data.startDate} - ${data.endDate}` : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            {/* Fallback message if no athletic experience */}
            {(!studentToDisplay.athleticsSportsProfile || Object.keys(studentToDisplay.athleticsSportsProfile).length === 0) &&
             (!studentToDisplay.athleticsAwardsProfile || Object.keys(studentToDisplay.athleticsAwardsProfile).length === 0) && (
              <Typography variant="body2" color="text.secondary">No athletic experience listed.</Typography>
            )}
          </Box>
        )}

        {/* Work & Volunteering Tab */}
        {selectedTab === 2 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0, mr: 1 }}>Work & Volunteering</Typography>
              <Tooltip title="Add Activity">
                <IconButton onClick={() => setShowAddWorkVolunteer(true)} color="primary">
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
            {studentToDisplay.workVolunteerExpProfile && Object.keys(studentToDisplay.workVolunteerExpProfile).length > 0 ? (
              <TableContainer component={Paper} sx={{ boxShadow: 'none', mt: 2, width: '100%' }}>
                <Table size="small" sx={{ fontSize: 14, tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 120, width: 120 }}>Position</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 120, width: 120 }}>Employer</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 80, width: 80 }}>Grades</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 200, width: 200 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 80, width: 80 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(studentToDisplay.workVolunteerExpProfile).map(([key, data], idx) => (
                      <TableRow key={key} sx={{ backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50' }}>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 120, width: 120 }}>{data.position || ''}</TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 120, width: 120 }}>{data.employer || ''}</TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 80, width: 80 }}>
                          {(() => {
                            const start = data.startGrade;
                            const end = data.endGrade;
                            if (!start) return '';
                            if (!end || start === end) return start;
                            if (end > start) return `${start}-${end}`;
                            return start;
                          })()}
                        </TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 200, width: 200 }}>{data.description || ''}</TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 80, width: 80, textAlign: 'right' }}>
                          <Tooltip title="Remove">
                            <IconButton 
                              onClick={() => handleWorkVolunteerDeleteClick(key, data.position || 'this activity')}
                              disabled={isSaving}
                              color="primary"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">No work or volunteer experience listed.</Typography>
            )}
          </Box>
        )}

        {/* Clubs & Activities Tab */}
        {selectedTab === 3 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0, mr: 1 }}>Clubs & Activities</Typography>
              <Tooltip title="Add Activity">
                <IconButton onClick={() => setShowAddActivity(true)} color="primary">
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
            {studentToDisplay.interestsActivitiesProfile && Object.keys(studentToDisplay.interestsActivitiesProfile).length > 0 ? (
              <TableContainer component={Paper} sx={{ boxShadow: 'none', mt: 2, width: '100%' }}>
                <Table size="small" sx={{ fontSize: 14, tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 200, width: 200 }}>Club/Activity</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 100, width: 100 }}>Grade</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 150, width: 150 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 60, width: 60 }}>Link</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 14, minWidth: 100, width: 100 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(studentToDisplay.interestsActivitiesProfile).map(([key, data], idx) => (
                      <TableRow key={key} sx={{ backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50' }}>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 200, width: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.interest || ''}</TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 100, width: 100 }}>
                          {(() => {
                            const start = data.startGrade;
                            const end = data.endGrade;
                            if (!start) return '';
                            if (!end || start === end) return start;
                            if (end > start) return `${start}-${end}`;
                            return start;
                          })()}
                        </TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 150, width: 150 }}>{data.description || ''}</TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 60, width: 60 }}>
                          {data.link ? (
                            <a href={data.link} target="_blank" rel="noopener noreferrer">Link</a>
                          ) : ''}
                        </TableCell>
                        <TableCell sx={{ py: 1, px: 2, minWidth: 100, width: 100, textAlign: 'right' }}>
                          <Tooltip title="Remove">
                            <IconButton 
                              onClick={() => handleDeleteClick(key, data.interest || 'this activity')}
                              disabled={isSaving}
                              color="primary"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">No clubs or activities listed.</Typography>
            )}
          </Box>
        )}

        {/* Account Tab */}
        {selectedTab === 4 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0, mr: 1 }}>Account Information</Typography>
              {!isEditingAccount && !isGuidanceUser && (
                <Tooltip title="Edit Account Information">
                  <IconButton onClick={() => setIsEditingAccount(true)} color="primary">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {isEditingAccount && !isGuidanceUser ? (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="First Name"
                    value={accountFormData.firstName}
                    onChange={(e) => handleAccountInputChange('firstName', e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                  />
                  <TextField
                    label="Last Name"
                    value={accountFormData.lastName}
                    onChange={(e) => handleAccountInputChange('lastName', e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 244, maxWidth: 244 }}
                    options={highSchools}
                    getOptionLabel={(option) => option.name}
                    value={highSchools.find(school => school.name === accountFormData.schoolName) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        handleHighSchoolChange(newValue.name);
                      } else {
                        handleHighSchoolChange('');
                      }
                    }}
                      disabled={loadingSchools}
                    loading={loadingSchools}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="High School"
                        placeholder="Type to search for your school..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingSchools ? 'Loading...' : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2">
                            {option.name}
                          </Typography>
                          {option.city && (
                            <Typography variant="caption" color="text.secondary">
                              {option.city}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      
                      const filtered = options.filter(option =>
                        option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                        (option.city && option.city.toLowerCase().includes(inputValue.toLowerCase()))
                      );
                      
                      // If no matches found, return empty array
                      return filtered;
                    }}
                    isOptionEqualToValue={(option, value) => option.name === value?.name}
                    noOptionsText="No schools found"
                    clearOnEscape
                    selectOnFocus
                    handleHomeEndKeys
                  />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Grade</InputLabel>
                    <Select
                      value={accountFormData.grade}
                      label="Grade"
                      onChange={(e) => handleAccountInputChange('grade', e.target.value)}
                    >
                      <MenuItem value={9}>Grade 9</MenuItem>
                      <MenuItem value={10}>Grade 10</MenuItem>
                      <MenuItem value={11}>Grade 11</MenuItem>
                      <MenuItem value={12}>Grade 12</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="City"
                    value={accountFormData.city}
                    onChange={(e) => handleAccountInputChange('city', e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                  />
                  <TextField
                    label="Province"
                    value={accountFormData.province}
                    onChange={(e) => handleAccountInputChange('province', e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                  />
                  {accountFormData.country === 'US' && (
                    <TextField
                      label="Country"
                      value={accountFormData.country}
                      onChange={(e) => handleAccountInputChange('country', e.target.value)}
                      size="small"
                      sx={{ minWidth: 120 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={accountFormData.email}
                    onChange={(e) => handleAccountInputChange('email', e.target.value)}
                    size="small"
                    type="email"
                    error={!!emailError}
                    helperText={emailError}
                  />
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={accountFormData.phoneNumber}
                    onChange={(e) => handleAccountInputChange('phoneNumber', e.target.value)}
                    size="small"
                    placeholder="e.g., (555) 123-4567"
                    error={!!phoneError}
                    helperText={phoneError}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Primary Language</InputLabel>
                    <Select
                      value={accountFormData.primaryLanguage}
                      label="Primary Language"
                      onChange={(e: any) => handleAccountInputChange('primaryLanguage', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select primary language</em>
                      </MenuItem>
                      <MenuItem value="English">English</MenuItem>
                      <MenuItem value="French">French</MenuItem>
                      <MenuItem value="Both">Both</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={accountFormData.status}
                      label="Status"
                      onChange={(e: any) => handleAccountInputChange('status', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select status</em>
                      </MenuItem>
                      <MenuItem value="Researching Undergraduate">Researching Undergraduate</MenuItem>
                      <MenuItem value="Guidance">Guidance</MenuItem>
                      <MenuItem value="Parent">Parent</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={accountFormData.gender}
                      label="Gender"
                      onChange={(e: any) => handleAccountInputChange('gender', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select gender</em>
                      </MenuItem>
                      <MenuItem value="F">F</MenuItem>
                      <MenuItem value="M">M</MenuItem>
                      <MenuItem value="MYOB">MYOB</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Account Type</InputLabel>
                    <Select
                      value={accountFormData.accountType}
                      label="Account Type"
                      onChange={(e: any) => handleAccountInputChange('accountType', e.target.value)}
                    >
                      <MenuItem value={1}>Student</MenuItem>
                      <MenuItem value={2}>Parent</MenuItem>
                      <MenuItem value={3}>Guidance</MenuItem>
                      <MenuItem value={4}>Other</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={accountFormData.isInternational}
                        onChange={(e) => handleAccountInputChange('isInternational', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="International Student"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Instagram"
                    value={accountFormData.instagramUrl}
                    onChange={(e) => handleAccountInputChange('instagramUrl', e.target.value)}
                    size="small"
                    placeholder="https://instagram.com/username"
                    type="url"
                    InputProps={{
                      startAdornment: (
                        <InstagramIcon sx={{ mr: 1, color: '#E4405F' }} />
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="X (Twitter)"
                    value={accountFormData.twitterUrl}
                    onChange={(e) => handleAccountInputChange('twitterUrl', e.target.value)}
                    size="small"
                    placeholder="https://twitter.com/username"
                    type="url"
                    InputProps={{
                      startAdornment: (
                        <TwitterIcon sx={{ mr: 1, color: '#1DA1F2' }} />
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="LinkedIn"
                    value={accountFormData.linkedinUrl}
                    onChange={(e) => handleAccountInputChange('linkedinUrl', e.target.value)}
                    size="small"
                    placeholder="https://linkedin.com/in/username"
                    type="url"
                    InputProps={{
                      startAdornment: (
                        <LinkedInIcon sx={{ mr: 1, color: '#0077B5' }} />
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveAccount}
                    disabled={isSavingAccount}
                  >
                    {isSavingAccount ? <CircularProgress size={20} /> : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancelAccount}
                    disabled={isSavingAccount}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Phone Number</Typography>
                      <Typography variant="body2">
                        {currentUser?.phoneNumber || ''}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                      <Typography variant="body2">
                        {currentUser?.email || authUser?.email || ''}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                {currentUser?.country === 'US' && (
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                        <Typography variant="body2">
                          {currentUser?.country}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Primary Language</Typography>
                      <Typography variant="body2">
                        {currentUser?.primaryLanguage || ''}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                      <Typography variant="body2">
                        {currentUser?.status || ''}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                      <Typography variant="body2">
                        {currentUser?.gender || ''}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Account Type</Typography>
                      <Typography variant="body2">
                        {currentUser?.userType === 1 ? 'Student' : 
                         currentUser?.userType === 2 ? 'Parent' : 
                         currentUser?.userType === 3 ? 'Guidance' : 
                         currentUser?.userType === 4 ? 'Other' : ''}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">International Student</Typography>
                      <Typography variant="body2">
                        {currentUser?.isInternational ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <InstagramIcon sx={{ mr: 1, color: '#E4405F', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="text.secondary">Instagram</Typography>
                      </Box>
                      <Typography variant="body2">
                        {currentUser?.instagramUrl ? (
                          <a href={currentUser.instagramUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                            {currentUser.instagramUrl}
                          </a>
                        ) : ''}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TwitterIcon sx={{ mr: 1, color: '#1DA1F2', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="text.secondary">X (Twitter)</Typography>
                      </Box>
                      <Typography variant="body2">
                        {currentUser?.twitterUrl ? (
                          <a href={currentUser.twitterUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                            {currentUser.twitterUrl}
                          </a>
                        ) : ''}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LinkedInIcon sx={{ mr: 1, color: '#0077B5', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="text.secondary">LinkedIn</Typography>
                      </Box>
                      <Typography variant="body2">
                        {currentUser?.linkedinUrl ? (
                          <a href={currentUser.linkedinUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                            {currentUser.linkedinUrl}
                          </a>
                        ) : ''}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </Paper>



      {/* Add Activity Dialog */}
      <Dialog 
        open={showAddActivity} 
        onClose={() => setShowAddActivity(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Activity</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Activity Name *"
              value={newActivity.interest}
              onChange={(e) => setNewActivity(prev => ({ ...prev, interest: e.target.value }))}
              fullWidth
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Grade"
                value={newActivity.startGrade}
                onChange={(e) => setNewActivity(prev => ({ ...prev, startGrade: e.target.value }))}
                fullWidth
                placeholder="e.g., 9"
              />
              <TextField
                label="End Grade"
                value={newActivity.endGrade}
                onChange={(e) => setNewActivity(prev => ({ ...prev, endGrade: e.target.value }))}
                fullWidth
                placeholder="e.g., 12"
              />
            </Box>
            <TextField
              label="Description"
              value={newActivity.description}
              onChange={(e) => setNewActivity(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe your involvement in this activity..."
            />
            <TextField
              label="Link (Optional)"
              value={newActivity.link}
              onChange={(e) => setNewActivity(prev => ({ ...prev, link: e.target.value }))}
              fullWidth
              placeholder="https://..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddActivity(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddActivity} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Add Activity'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Work/Volunteering Activity Dialog */}
      <Dialog 
        open={showAddWorkVolunteer} 
        onClose={() => setShowAddWorkVolunteer(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Work/Volunteering Activity</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Position Title *"
              value={newWorkVolunteer.position}
              onChange={(e) => setNewWorkVolunteer(prev => ({ ...prev, position: e.target.value }))}
              fullWidth
              required
              placeholder="e.g., Cashier, Volunteer Coordinator"
            />
            <TextField
              label="Employer/Organization"
              value={newWorkVolunteer.employer}
              onChange={(e) => setNewWorkVolunteer(prev => ({ ...prev, employer: e.target.value }))}
              fullWidth
              placeholder="e.g., McDonald's, Local Food Bank"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Grade"
                value={newWorkVolunteer.startGrade}
                onChange={(e) => setNewWorkVolunteer(prev => ({ ...prev, startGrade: e.target.value }))}
                fullWidth
                placeholder="e.g., 9"
              />
              <TextField
                label="End Grade"
                value={newWorkVolunteer.endGrade}
                onChange={(e) => setNewWorkVolunteer(prev => ({ ...prev, endGrade: e.target.value }))}
                fullWidth
                placeholder="e.g., 12"
              />
            </Box>
            <TextField
              label="Description"
              value={newWorkVolunteer.description}
              onChange={(e) => setNewWorkVolunteer(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe your responsibilities and achievements..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddWorkVolunteer(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddWorkVolunteer} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Add Activity'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Work/Volunteering Delete Confirmation Dialog */}
      <Dialog 
        open={showDeleteWorkVolunteerConfirmation} 
        onClose={handleWorkVolunteerCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{workVolunteerToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWorkVolunteerCancelDelete} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleWorkVolunteerConfirmDelete} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Academic Awards & Honours Dialog */}
      <Dialog 
        open={showAddAwardsHonours} 
        onClose={() => setShowAddAwardsHonours(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Academic {getAwardHonourLabel()}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={`${getAwardHonourLabel()} Title *`}
              value={newAwardsHonours.title}
              onChange={(e) => setNewAwardsHonours(prev => ({ ...prev, title: e.target.value }))}
              fullWidth
              required
              placeholder="e.g., Honour Roll, Academic Excellence Award, Dean's List"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Grade (Optional)"
                value={newAwardsHonours.grade}
                onChange={(e) => setNewAwardsHonours(prev => ({ ...prev, grade: e.target.value }))}
                fullWidth
                placeholder="e.g., 11, 12"
              />
              <TextField
                label="Year Received (Optional)"
                value={newAwardsHonours.yearReceived}
                onChange={(e) => setNewAwardsHonours(prev => ({ ...prev, yearReceived: e.target.value }))}
                fullWidth
                placeholder="e.g., 2023, 2024"
              />
            </Box>
            <TextField
              label="Description (Optional)"
              value={newAwardsHonours.description}
              onChange={(e) => setNewAwardsHonours(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe the award, criteria, or any additional details..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddAwardsHonours(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddAwardsHonours} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : `Add ${getAwardHonourLabel()}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Academic Extenuating Circumstances Dialog */}
      <Dialog 
        open={showAddExtenuatingCircumstances} 
        onClose={() => setShowAddExtenuatingCircumstances(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Extenuating Circumstance</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Description *"
              value={newExtenuatingCircumstances.description}
              onChange={(e) => setNewExtenuatingCircumstances(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              required
              multiline
              rows={4}
              placeholder="Describe the extenuating circumstance that affected your academic performance..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddExtenuatingCircumstances(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddExtenuatingCircumstances} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Add Circumstance'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Athletics Activity Dialog */}
      <Dialog 
        open={showAddAthletics} 
        onClose={() => setShowAddAthletics(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Athletics Activity</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Sport Name *"
              value={newAthletics.sport}
              onChange={(e) => setNewAthletics(prev => ({ ...prev, sport: e.target.value }))}
              fullWidth
              required
              placeholder="e.g., Basketball, Soccer, Swimming"
            />
            <TextField
              label="Position (Optional)"
              value={newAthletics.position}
              onChange={(e) => setNewAthletics(prev => ({ ...prev, position: e.target.value }))}
              fullWidth
              placeholder="e.g., Point Guard, Captain, Goalie"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Grade"
                value={newAthletics.startGrade}
                onChange={(e) => setNewAthletics(prev => ({ ...prev, startGrade: e.target.value }))}
                fullWidth
                placeholder="e.g., 9"
              />
              <TextField
                label="End Grade"
                value={newAthletics.endGrade}
                onChange={(e) => setNewAthletics(prev => ({ ...prev, endGrade: e.target.value }))}
                fullWidth
                placeholder="e.g., 12"
              />
            </Box>
            <TextField
              label="Description"
              value={newAthletics.description}
              onChange={(e) => setNewAthletics(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe your involvement, achievements, or role in this sport..."
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Jersey Number (Optional)"
                value={newAthletics.jerseyNumber}
                onChange={(e) => setNewAthletics(prev => ({ ...prev, jerseyNumber: e.target.value }))}
                fullWidth
                placeholder="e.g., 23"
              />
              <Select
                label="Level"
                value={newAthletics.level}
                onChange={(e) => setNewAthletics(prev => ({ ...prev, level: e.target.value }))}
                fullWidth
                displayEmpty
                renderValue={(value) => value || 'Select Level'}
              >
                <MenuItem value="">Select Level</MenuItem>
                <MenuItem value="Club">Club</MenuItem>
                <MenuItem value="Varsity">Varsity</MenuItem>
                <MenuItem value="Junior Varsity">Junior Varsity</MenuItem>
                <MenuItem value="Freshman">Freshman</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </Box>
            <TextField
              label="YouTube Link (Optional)"
              value={newAthletics.youtubeLink}
              onChange={(e) => setNewAthletics(prev => ({ ...prev, youtubeLink: e.target.value }))}
              fullWidth
              placeholder="https://www.youtube.com/..."
            />
            <TextField
              label="Article Link (Optional)"
              value={newAthletics.articleLink}
              onChange={(e) => setNewAthletics(prev => ({ ...prev, articleLink: e.target.value }))}
              fullWidth
              placeholder="https://..."
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                id="isContinue"
                checked={newAthletics.isContinue}
                onChange={(e) => setNewAthletics(prev => ({ ...prev, isContinue: e.target.checked }))}
                style={{ margin: 0 }}
              />
              <label htmlFor="isContinue" style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>
                Interested in playing this sport in university/college
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddAthletics(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddAthletics} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Add Activity'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Athletics Delete Confirmation Dialog */}
      <Dialog 
        open={showDeleteAthleticsConfirmation} 
        onClose={handleAthleticsCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{athleticsToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAthleticsCancelDelete} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleAthleticsConfirmDelete} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Academic Awards & Honours Delete Confirmation Dialog */}
      <Dialog 
        open={showDeleteAwardsHonoursConfirmation} 
        onClose={handleAwardsHonoursCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{awardsHonoursToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAwardsHonoursCancelDelete} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleAwardsHonoursConfirmDelete} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Academic Extenuating Circumstances Delete Confirmation Dialog */}
      <Dialog 
        open={showDeleteExtenuatingCircumstancesConfirmation} 
        onClose={handleExtenuatingCircumstancesCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{extenuatingCircumstancesToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExtenuatingCircumstancesCancelDelete} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleExtenuatingCircumstancesConfirmDelete} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={showDeleteConfirmation} 
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{activityToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message / Progress Indicator - Only show completion messages, not progress */}
      <Snackbar
        open={showSuccessMessage && !isRecalculatingEligibility}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          severity="success"
          sx={{
            backgroundColor: '#4caf50',
            color: 'white',
            fontWeight: 'bold',
            minWidth: '300px',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={showErrorMessage}
        autoHideDuration={6000}
        onClose={() => setShowErrorMessage(false)}
        message={errorMessage}
      />

      {/* Progress indicator is now handled by the Snackbar below */}
      
    </Box>
  );
};

export default UserStudentProfile;
